// src/data/CatalogData.ts
import categoryInjection from "@/assets/category-injection.png";
import categoryEquipment from "@/assets/category-equipment.png";
import categorySurgery from "@/assets/category-surgery.png";
import categoryHygiene from "@/assets/category-hygiene.png";
import categoryDressings from "@/assets/sterilization.png";
import categoryLab from "@/assets/lab.png";

export type CatalogItem = {
  id: number;
  category: string;
  nameKey: string;
  descriptionKey?: string;
  price?: number;
  
  // IKPU коды для обработки платежей (Payme merchant IDs)
  ikpuCode?: string; // 16-digit Payme merchant ID для товара (по умолчанию для всех вариантов)
  
  variantIkpuCodes?: Record<string, string>; // Map of "color_size" => IKPU code
  
  // Size-specific IKPU коды (по размерам)
  // Format: "variants.sizes.1ml" => "507144111111111"
  sizeIkpuCodes?: Record<string, string>; // Map of "variants.sizes.XXX" => IKPU code
  
  // Size-specific package codes (коды упаковки по размерам)
  // Format: "variants.sizes.1ml" => "PKG001"
  sizePackageCodes?: Record<string, string>; // Map of "variants.sizes.XXX" => package code
  
  // Коды упаковки для каждого товара
  package_code?: string; // Код упаковки (для учета и инвентаризации)
  
  // НДС процент (по умолчанию 12%)
  vat_percent: number; // НДС процент, по умолчанию 12
  
  imageBase?: string;

  imageBases?: string[];

  colors?: string[];
  colorImages?: Record<string, string | string[]>; // Map color key to image filename(s) - supports multiple photos per color

  sizes?: string[];
  sizeImages?: Record<string, string | string[]>; // Map size key to image filename(s) - supports multiple photos per size
  sizePrices?: Record<string, number>; // Map color key to image sources - renders different images for each color
  sizesDescription?: Record<string, string>; // Map size key to description key - renders different descriptions for each size
};
export type ImageSources = {
  webp?: string;
  fallback: string;
};

const itemsImg = import.meta.glob(
  "/src/assets/items/**/*.{png,jpg,jpeg,webp}",
  { eager: true },
) as Record<string, { default: string }>;

export const getImageSources = (
  basename: string,
  category?: string,
): ImageSources => {
  const basePath = category
    ? `/src/assets/items/${category}/${basename}`
    : `/src/assets/items/${basename}`;

  const webp = itemsImg[`${basePath}.webp`]?.default;
  const jpg =
    itemsImg[`${basePath}.jpg`]?.default ||
    itemsImg[`${basePath}.jpeg`]?.default ||
    itemsImg[`${basePath}.png`]?.default;

  if (!webp && !jpg) {
    console.warn(`Image not found: ${basePath}`);
  }

  return {
    webp,
    fallback: webp || jpg || "",
  };
};

export const allItems: CatalogItem[] = [
  {
    id: 1,
    category: "injection",
    nameKey: "items.1.name",
    ikpuCode: "09018001080000000",
    package_code: "1460392",
    vat_percent: 12,
    sizeIkpuCodes: {
      "variants.sizes.1ml": "09018001080000000",
      "variants.sizes.2ml": "09018001080000000",
      "variants.sizes.5ml": "09018001080000000",
      "variants.sizes.10ml": "09018001080000000",
      "variants.sizes.10mlwtoutneedle": "09018001080000000",
      "variants.sizes.20ml": "09018001080000000",
      "variants.sizes.60mlLL": "09018001080000000",
      "variants.sizes.60mlCT": "09018001080000000",
    },
    sizes: [
      "variants.sizes.1ml",
      "variants.sizes.2ml",
      "variants.sizes.5ml",
      "variants.sizes.10ml",
      "variants.sizes.10mlwtoutneedle",
      "variants.sizes.20ml",
      "variants.sizes.60mlLL",
      "variants.sizes.60mlCT",
    ],
    sizePrices: {
      "variants.sizes.1ml": 540,
      "variants.sizes.2ml": 420,
      "variants.sizes.5ml": 448,
      "variants.sizes.10ml": 630,
      "variants.sizes.10mlwtoutneedle": 490,
      "variants.sizes.20ml": 1100,
      "variants.sizes.60mlLL": 5500,
      "variants.sizes.60mlCT": 5500,
    },
    sizesDescription: {
      "variants.sizes.1ml": "items.1.description.1ml",
      "variants.sizes.2ml": "items.1.description.2ml",
      "variants.sizes.5ml": "items.1.description.5ml",
      "variants.sizes.10ml": "items.1.description.10ml",
      "variants.sizes.10mlwtoutneedle": "items.1.description.10mlwtoutneedle",
      "variants.sizes.20ml": "items.1.description.20ml",
      "variants.sizes.60mlLL": "items.1.description.60mlLL",
      "variants.sizes.60mlCT": "items.1.description.60mlCT",
    },
    imageBases: [
      "шприцы 1мл UKM",
      "шприцы 2мл UKM",
      "шприцы 5мл UKM",
      "шприцы 10мл UKM-1",
      "Шприцы 60 мл LUER LOCK UKM",
    ],
    sizeImages: {
      "variants.sizes.1ml": "шприцы 1мл UKM",
      "variants.sizes.2ml": "шприцы 2мл UKM",
      "variants.sizes.5ml": "шприцы 5мл UKM",
      "variants.sizes.10ml": "шприцы 10мл UKM-1",
      "variants.sizes.10mlwtoutneedle": "шприцы 10мл UKM-2",
      "variants.sizes.60mlLL": "Шприцы 60 мл LUER LOCK UKM",
    },
  },
  {
    id: 2,
    category: "injection",
    nameKey: "items.2.name",
    ikpuCode: "09018001080000000",
    package_code: "1460392",
    vat_percent: 12,
    sizeIkpuCodes: {
      "variants.sizes.1ml": "09018001080000000",
      "variants.sizes.2ml": "09018001080000000",
      "variants.sizes.5ml": "09018001080000000",
      "variants.sizes.10ml": "09018001080000000",
    },
    sizes: [
      "variants.sizes.1ml",
      "variants.sizes.2ml",
      "variants.sizes.5ml",
      "variants.sizes.10ml",
    ],
    sizePrices: {
      "variants.sizes.1ml": 525,
      "variants.sizes.2ml": 390,
      "variants.sizes.5ml": 431,
      "variants.sizes.10ml": 543,
    },
    imageBases: ["шприцы MFA 2мл-1", "шприцы MFA 5мл-1", "шприцы MFA 10мл-1"],
    sizeImages: {
      "variants.sizes.2ml": "шприцы MFA 2мл-1",
      "variants.sizes.5ml": "шприцы MFA 5мл-1",
      "variants.sizes.10ml": "шприцы MFA 10мл-1",
    },
  },
  {
    id: 3,
    category: "injection",
    nameKey: "items.3.name",
    ikpuCode: "09018001080000000",
    package_code: "1460392",
    vat_percent: 12,
    sizeIkpuCodes: {
      "variants.sizes.1ml": "09018001080000000",
      "variants.sizes.2ml": "09018001080000000",
      "variants.sizes.5ml": "09018001080000000",
      "variants.sizes.10ml": "09018001080000000",
      "variants.sizes.20ml": "09018001080000000",
      "variants.sizes.50ml": "09018001080000000",
    },
    sizes: [
      "variants.sizes.1ml",
      "variants.sizes.2ml",
      "variants.sizes.5ml",
      "variants.sizes.10ml",
      "variants.sizes.20ml",
      "variants.sizes.50ml",
    ],
    imageBases: [
      "шприцы 1мл insulin Wego",
      "шприцы 2мл Wego",
      "шприцы 5мл Wego-1",
      "шприцы 10мл Wego-1",
      "шприцы 20мл Wego",
      "шприцы 50мл Wego",
    ],
    sizeImages: {
      "variants.sizes.1ml": "шприцы 1мл insulin Wego",
      "variants.sizes.2ml": "шприцы 2мл Wego",
      "variants.sizes.5ml": "шприцы 5мл Wego-1",
      "variants.sizes.10ml": "шприцы 10мл Wego-1",
      "variants.sizes.20ml": "шприцы 20мл Wego",
      "variants.sizes.50ml": "шприцы 50мл Wego",
    },
    sizePrices: {
      "variants.sizes.1ml": 510,
      "variants.sizes.2ml": 520,
      "variants.sizes.5ml": 580,
      "variants.sizes.10ml": 710,
      "variants.sizes.20ml": 1050,
      "variants.sizes.50ml": 6550,
    },
    sizesDescription: {
      "variants.sizes.1ml": "items.3.description.1ml",
      "variants.sizes.2ml": "items.3.description.2ml",
      "variants.sizes.5ml": "items.3.description.5ml",
      "variants.sizes.10ml": "items.3.description.10ml",
      "variants.sizes.20ml": "items.3.description.20ml",
      "variants.sizes.50ml": "items.3.description.50ml",
    },
  },
  {
    id: 4,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.4.name",
    ikpuCode: "09018001080000000",
    package_code: "1460392",
    imageBases: [
      "шприцы 1мл Uniject",
      "шприцы 2,5мл Uniject",
      "шприцы 5мл Uniject",
      "шприцы 10мл Uniject",
      "шприцы 20мл Uniject",
    ],
    sizes: [
      "variants.sizes.1ml",
      "variants.sizes.2p5ml",
      "variants.sizes.5ml",
      "variants.sizes.10ml",
      "variants.sizes.20ml",
    ],
    sizeImages: {
      "variants.sizes.1ml": "шприцы 1мл Uniject",
      "variants.sizes.2p5ml": "шприцы 2,5мл Uniject",
      "variants.sizes.5ml": "шприцы 5мл Uniject",
      "variants.sizes.10ml": "шприцы 10мл Uniject",
      "variants.sizes.20ml": "шприцы 20мл Uniject",
    },
    sizesDescription: {
      "variants.sizes.1ml": "items.4.description.1ml",
      "variants.sizes.2p5ml": "items.4.description.2p5ml",
      "variants.sizes.5ml": "items.4.description.5ml",
      "variants.sizes.10ml": "items.4.description.10ml",
      "variants.sizes.20ml": "items.4.description.20ml",
    },
    sizePrices: {
      "variants.sizes.1ml": 510,
      "variants.sizes.2p5ml": 385,
      "variants.sizes.5ml": 425,
      "variants.sizes.10ml": 540,
      "variants.sizes.20ml": 1000,
    },
  },
  {
    id: 5,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.5.name",
    ikpuCode: "09018001080000000",
    package_code: "1460392",
    imageBases: [
      "шприцы 2мл Healthline-1",
      "шприцы 3мл Healthline",
      "шприцы 5мл Healthline",
      "шприцы 10мл Healthline",
      "Шприцы 20 мл Healthline",
    ],
    sizes: [
      "variants.sizes.2ml",
      "variants.sizes.3ml",
      "variants.sizes.5ml",
      "variants.sizes.10ml",
      "variants.sizes.20ml",
    ],
    sizeImages: {
      "variants.sizes.2ml": "шприцы 2мл Healthline-1",
      "variants.sizes.3ml": "шприцы 3мл Healthline",
      "variants.sizes.5ml": "шприцы 5мл Healthline",
      "variants.sizes.10ml": "шприцы 10мл Healthline",
      "variants.sizes.20ml": "Шприцы 20 мл Healthline",
    },
    sizesDescription: {
      "variants.sizes.2ml": "items.5.description.2ml",
      "variants.sizes.3ml": "items.5.description.3ml",
      "variants.sizes.5ml": "items.5.description.5ml",
      "variants.sizes.10ml": "items.5.description.10ml",
      "variants.sizes.20ml": "items.5.description.20ml",
    },
    sizePrices: {
      "variants.sizes.2ml": 335,
      "variants.sizes.3ml": 365,
      "variants.sizes.5ml": 385,
      "variants.sizes.10ml": 550,
      "variants.sizes.20ml": 1200,
    },
  },
  {
    id: 6,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.6.name",
    descriptionKey: "items.6.description",
    imageBase: "детские шприцы 2мл Kiddi",
    ikpuCode: "09018001080000000",
    package_code: "1460392",
    price: 493,
  },
  {
    id: 7,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.7.name",
    ikpuCode: "09018001080000000",
    package_code: "1460392",
    imageBases: [
      "Шприцы 2.5 мл Westcom",
      "Шприцы 5.5 мл Westcom",
      "Шприцы 10.5 мл Westcom",
    ],
    sizes: [
      "variants.sizes.2p5ml",
      "variants.sizes.5p5ml",
      "variants.sizes.10p5ml",
    ],
    sizeImages: {
      "variants.sizes.2p5ml": "Шприцы 2.5 мл Westcom",
      "variants.sizes.5p5ml": "Шприцы 5.5 мл Westcom",
      "variants.sizes.10p5ml": "Шприцы 10.5 мл Westcom",
    },
    sizePrices: {
      "variants.sizes.2p5ml": 560,
      "variants.sizes.5p5ml": 555,
      "variants.sizes.10p5ml": 812,
    },
    sizesDescription: {
      "variants.sizes.2p5ml": "items.7.description.2p5ml",
      "variants.sizes.5p5ml": "items.7.description.5p5ml",
      "variants.sizes.10p5ml": "items.7.description.10p5ml",
    },
  },
  {
    id: 8,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.8.name",
    ikpuCode: "09018001080000000",
    package_code: "1460392",
    imageBases: ["Шприцы  1 мл инсулин WEGO (2)"],
    sizes: [
      "variants.sizes.0p3ml",
      "variants.sizes.0p5ml",
      "variants.sizes.1ml12mm",
      "variants.sizes.1ml8mm",
    ],
    sizesDescription: {
      "variants.sizes.0p3ml": "items.8.description.0p3ml",
      "variants.sizes.0p5ml": "items.8.description.0p5ml",
      "variants.sizes.1ml12mm": "items.8.description.1ml12mm",
      "variants.sizes.1ml8mm": "items.8.description.1ml8mm",
    },
    price: 1360,
  },
  {
    id: 9,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.9.name",
    ikpuCode: "09018001080000000",
    package_code: "1460392",
    descriptionKey: "items.9.description",
    imageBase: "Шприцы инсулиновый 1 мл UKM",
    price: 550,
  },
  {
    id: 10,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.10.name",
    ikpuCode: "09018001080000000",
    package_code: "1460392",
    descriptionKey: "items.10.description",
    imageBase: "шприцы инсулиновый Healthline-1",
    price: 770,
  },
  {
    id: 11,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.11.name",
    ikpuCode: "09018001080000000",
    package_code: "1460392",
    imageBases: [
      "Шприц KD-JECT III без иглы, с наконечником 50 мл",
      "Шприц KD-JECT III без иглы, с наконечником 100 мл",
    ],
    sizes: [
      "variants.sizes.50mlLL",
      "variants.sizes.50mlWC",
      "variants.sizes.100mlWC",
    ],
    sizeImages: {
      "variants.sizes.50mlWC":
        "Шприц KD-JECT III без иглы, с наконечником 50 мл",
      "variants.sizes.100mlWC":
        "Шприц KD-JECT III без иглы, с наконечником 100 мл",
    },
    sizePrices: {
      "variants.sizes.50mlLL": 10500,
      "variants.sizes.50mlWC": 10500,
      "variants.sizes.100mlWC": 25000,
    },
    sizesDescription: {
      "variants.sizes.50mlLL": "items.11.description.withoutneedleLuerLock",
      "variants.sizes.50mlWC": "items.11.description.withneedleWithCap",
      "variants.sizes.100mlWC":
        "items.11.description.withneedleWithoutCap100ml",
    },
  },
  {
    id: 12,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.12.name",
    ikpuCode: "09018001080000000",
    package_code: "1460392",
    descriptionKey: "items.12.description",
    imageBase: "Шприц c Иглой 50 мл LDH (2)",
    price: 6000,
  },
  {
    id: 13,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.13.name",
    descriptionKey: "items.13.description",
    sizes: ["variants.sizes.27g", "variants.sizes.30g"],
    sizePrices: {
      "variants.sizes.27g": 530,
      "variants.sizes.30g": 530,
    },
    imageBases: ["Игла стерильная 27 G", "Игла стерильная 30G"],
    sizeImages: {
      "variants.sizes.27g": "Игла стерильная 27 G",
      "variants.sizes.30g": "Игла стерильная 30G",
    },
  },
  {
    id: 14,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.14.name",
    ikpuCode: "09018001014000000",
    package_code: "1460360",
    descriptionKey: "items.14.description",
    imageBase: "система инфузионная Westcom",
    price: 1900,
  },
  {
    id: 15,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.15.name",
    ikpuCode: "09018001014000000",
    package_code: "1460360",
    descriptionKey: "items.15.description",
    imageBase: "Система инфузионная WEGO",
    price: 1800,
  },
  {
    id: 16,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.16.name",
    ikpuCode: "09018001014000000",
    package_code: "1460360",
    descriptionKey: "items.16.description",
    imageBase: "Система инфузионная MC UNISET",
    price: 1550,
  },
  {
    id: 17,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.17.name",
    ikpuCode: "09018001014000000",
    package_code: "1460360",
    descriptionKey: "items.17.description",
    imageBase: "Система трансфузионная",
    price: 6850,
  },
  {
    id: 18,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.18.name",
    ikpuCode: "09018001014000000",
    package_code: "1460360",
    descriptionKey: "items.18.description",
    imageBase: "Система инфузионная ISV-A",
    price: 6850,
  },
  {
    id: 19,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.18.name",
    ikpuCode: "09018001014000000",
    package_code: "1460360",
    descriptionKey: "items.18.description",
    imageBase: "Системы Инфузионные(TBL-150)",
    price: 1550,
  },
  {
    id: 20,
    vat_percent: 12,
    category: "equipment",
    nameKey: "items.20.name",
    ikpuCode: "04015001003000000",
    package_code: "1361414",
    descriptionKey: "items.20.description",
    imageBases: [
      "Перчатки нитриловые размер M цвет белый",
      "Перчатки нитриловые размер M цвет синий",
      "Перчатки нитриловые размер M цвет розовый",
      "Перчатки нитриловые размер M цвет фиолетовый",
      "Перчатки нитриловые размер M цвет черный",
      "Перчатки нитриловые размер M цвет голубой",
      "Перчатки нитриловые размер M цвет светло-розовый",
    ],
    colors: [
      "variants.colors.white",
      "variants.colors.blue",
      "variants.colors.pink",
      "variants.colors.purple",
      "variants.colors.black",
      "variants.colors.lightBlue",
      "variants.colors.lightPink",
    ],
    colorImages: {
      "variants.colors.white": "Перчатки нитриловые размер M цвет белый",
      "variants.colors.blue": "Перчатки нитриловые размер M цвет синий",
      "variants.colors.pink": "Перчатки нитриловые размер M цвет розовый",
      "variants.colors.purple": "Перчатки нитриловые размер M цвет фиолетовый",
      "variants.colors.black": "Перчатки нитриловые размер M цвет черный",
      "variants.colors.lightBlue": "Перчатки нитриловые размер M цвет голубой",
      "variants.colors.lightPink":
        "Перчатки нитриловые размер M цвет светло-розовый",
    },
    sizes: [
      "variants.sizes.xs",
      "variants.sizes.s",
      "variants.sizes.m",
      "variants.sizes.l",
      "variants.sizes.xl",
    ],
    price: 40000,
  },
  {
    id: 21,
    vat_percent: 12,
    category: "equipment",
    nameKey: "items.21.name",
    ikpuCode: "04015001003000000",
    package_code: "1361414",
    descriptionKey: "items.21.description",
    imageBases: [
      "Перчатки нитриловые размер M черный цвет ADRAS GLOVES",
      "Перчатки нитриловые размер M синий цвет ADRAS GLOVES",
    ],
    colors: ["variants.colors.black", "variants.colors.blue"],
    colorImages: {
      "variants.colors.black":
        "Перчатки нитриловые размер M черный цвет ADRAS GLOVES",
      "variants.colors.blue":
        "Перчатки нитриловые размер M синий цвет ADRAS GLOVES",
    },
    sizes: [
      "variants.sizes.xs",
      "variants.sizes.s",
      "variants.sizes.m",
      "variants.sizes.l",
      "variants.sizes.xl",
    ],
    price: 36000,
  },
  {
    id: 22,
    vat_percent: 12,
    category: "equipment",
    nameKey: "items.22.name",
    ikpuCode: "04015001003000000",
    package_code: "1361414",
    descriptionKey: "items.22.description",
    imageBases: [
      "Перчатки нитриловые PREMIUM размер M синий цвет",
      "Перчатки нитриловые PREMIUM размер M черный цвет",
    ],
    colors: ["variants.colors.blue", "variants.colors.black"],
    colorImages: {
      "variants.colors.blue": "Перчатки нитриловые PREMIUM размер M синий цвет",
      "variants.colors.black":
        "Перчатки нитриловые PREMIUM размер M черный цвет",
    },
    sizes: ["variants.sizes.s", "variants.sizes.m", "variants.sizes.l"],
    price: 48000,
  },
  {
    id: 23,
    vat_percent: 12,
    category: "equipment",
    nameKey: "items.23.name",
    ikpuCode: "04015001004000000",
    package_code: "1342729",
    descriptionKey: "items.23.description",
    imageBases: [
      "перчатки стер.хир Dentafil размер 7,0",
      "перчатки стер.хир Dentafil размер 7,5",
      "перчатки стер.хир Dentafil размер 8,0",
    ],
    sizes: ["variants.sizes.7p0", "variants.sizes.7p5", "variants.sizes.8p0"],
    sizeImages: {
      "variants.sizes.7p0": "перчатки стер.хир Dentafil размер 7,0",
      "variants.sizes.7p5": "перчатки стер.хир Dentafil размер 7,5",
      "variants.sizes.8p0": "перчатки стер.хир Dentafil размер 8,0",
    },
    price: 2900,
  },
  {
    id: 24,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.24.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.24.description",
    imageBases: [
      "Внутривенный катетер Ангиокатетер размер - 16G (2)",
      "Внутривенный катетер Ангиокатетер размер - 18G",
      "Внутривенный катетер Ангиокатетер размер - 20G",
      "Внутривенный катетер Ангиокатетер размер - 22G",
      "Внутривенный катетер Ангиокатетер размер - 24G",
    ],
    sizes: [
      "variants.sizes.16g",
      "variants.sizes.18g",
      "variants.sizes.20g",
      "variants.sizes.22g",
      "variants.sizes.24g",
      "variants.sizes.26g",
    ],
    sizeImages: {
      "variants.sizes.16g":
        "Внутривенный катетер Ангиокатетер размер - 16G (2)",
      "variants.sizes.18g": "Внутривенный катетер Ангиокатетер размер - 18G",
      "variants.sizes.20g": "Внутривенный катетер Ангиокатетер размер - 20G",
      "variants.sizes.22g": "Внутривенный катетер Ангиокатетер размер - 22G",
      "variants.sizes.24g": "Внутривенный катетер Ангиокатетер размер - 24G",
    },
    price: 3100,
  },
  {
    id: 25,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.25.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.25.description",
    imageBases: [
      "Внутривенный катетер Ангиокатетер размер - 20G",
      "Внутривенный катетер Ангиокатетер размер - 22G",
    ],
    sizes: ["variants.sizes.20g", "variants.sizes.22g"],
    sizeImages: {
      "variants.sizes.20g": "Внутривенный катетер Ангиокатетер размер - 20G",
      "variants.sizes.22g": "Внутривенный катетер Ангиокатетер размер - 22G",
    },
    price: 4100,
  },
  {
    id: 26,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.26.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.26.description",
    imageBases: [
      "Внутривенный катетер (Ангиокатетер) KD-FIX размер - 18G",
      "Внутривенный катетер (Ангиокатетер) KD-FIX размер - 20G",
      "Внутривенный катетер (Ангиокатетер) KD-FIX размер - 22G",
      "Внутривенный катетер (Ангиокатетер) KD-FIX размер - 24G",
    ],
    sizes: [
      "variants.sizes.18g",
      "variants.sizes.20g",
      "variants.sizes.22g",
      "variants.sizes.24g",
      "variants.sizes.26g",
    ],
    sizeImages: {
      "variants.sizes.18g":
        "Внутривенный катетер (Ангиокатетер) KD-FIX размер - 18G",
      "variants.sizes.20g":
        "Внутривенный катетер (Ангиокатетер) KD-FIX размер - 20G",
      "variants.sizes.22g":
        "Внутривенный катетер (Ангиокатетер) KD-FIX размер - 22G",
      "variants.sizes.24g":
        "Внутривенный катетер (Ангиокатетер) KD-FIX размер - 24G",
    },
    price: 5620,
  },
  {
    id: 27,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.27.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.27.description",
    imageBases: [
      "катетор бабочка KD-FLY 22G",
      "катетор бабочка KD-FLY 23G",
      "катетор бабочка KD-FLY 24G (2)",
      "катетор бабочка KD-FLY 25G (3)",
      "катетор бабочка KD-FLY 26G",
    ],
    sizes: [
      "variants.sizes.22g",
      "variants.sizes.23g",
      "variants.sizes.24g",
      "variants.sizes.25G",
      "variants.sizes.26g",
    ],
    sizeImages: {
      "variants.sizes.22g": "катетор бабочка KD-FLY 22G",
      "variants.sizes.23g": "катетор бабочка KD-FLY 23G",
      "variants.sizes.24g": "катетор бабочка KD-FLY 24G (2)",
      "variants.sizes.25G": "катетор бабочка KD-FLY 25G (3)",
      "variants.sizes.26g": "катетор бабочка KD-FLY 26G",
    },
    price: 1400,
  },
  {
    id: 28,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.28.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.28.description",
    imageBases: [
      "Катетер Игла бабочка Healthline размер 21G",
      "Катетер Игла бабочка Healthline размер 22G",
      "Катетер Игла бабочка  Healthline размер 25G",
    ],
    sizes: ["variants.sizes.21g", "variants.sizes.22g", "variants.sizes.25G"],
    sizeImages: {
      "variants.sizes.21g": "Катетер Игла бабочка Healthline размер 21G",
      "variants.sizes.22g": "Катетер Игла бабочка Healthline размер 22G",
      "variants.sizes.25G": "Катетер Игла бабочка  Healthline размер 25G",
    },
    price: 1150,
  },
  {
    id: 29,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.29.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.29.description",
    imageBases: [
      "канюля кислородные BEROTUBE размер детский",
      "канюля кислородные BEROTUBE размер детский-1",
    ],
    price: 16000,
  },
  {
    id: 30,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.30.name",
    ikpuCode: "09018001015000000",
    package_code: "1460362",
    descriptionKey: "items.30.description",
    imageBase: "Скарификатор копье",
    price: 165,
  },
  {
    id: 31,
    vat_percent: 12,
    category: "surgery",
    nameKey: "items.31.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.31.description",
    imageBases: [
      "Катетер Фолея 2-х ход 8 CHFR (2 way 30 ml)",
      "Катетер Фолея 2-х ход 10 CHFR (2 way 30 ml) (2)",
      "Катетер Фолея 2-х ход 18 CHFR (2 way 30 ml) (2)",
      "Катетер Фолея 2-х ход 20 CHFR (2 way 30 ml) ltd kitay",
      "Катетер Фолея 2-х ход 22, CHFR (2 way 30 ml)",
      "Катетер Фолея 2-х ход,24 CHFR (2 way 30 ml)",
    ],
    sizes: [
      "variants.sizes.14chfr",
      "variants.sizes.16chfr",
      "variants.sizes.18chfr",
      "variants.sizes.20chfr",
      "variants.sizes.22chfr",
      "variants.sizes.24chfr",
    ],
    sizeImages: {
      "variants.sizes.14chfr": "Катетер Фолея 2-х ход 6 CHFR (2 way 30 ml)",
      "variants.sizes.16chfr": "Катетер Фолея 2-х ход 10 CHFR (2 way 30 ml)",
      "variants.sizes.18chfr":
        "Катетер Фолея 2-х ход 18 CHFR (2 way 30 ml) (2)",
      "variants.sizes.20chfr":
        "Катетер Фолея 2-х ход 20 CHFR (2 way 30 ml) ltd kitay",
      "variants.sizes.22chfr": "Катетер Фолея 2-х ход 22, CHFR (2 way 30 ml)",
      "variants.sizes.24chfr": "Катетер Фолея 2-х ход,24 CHFR (2 way 30 ml)",
    },
    price: 112000,
  },
  {
    id: 32,
    vat_percent: 12,
    category: "surgery",
    nameKey: "items.32.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.32.description",
    imageBases: [
      "Катетер Фолея 2-х ход 16 CHFR (2 way 30 ml) Kdm medical (2)",
      "Катетер Фолея 2-х ход 18 CHFR (2 way 30 ml) Kdm medical (2)",
    ],
    sizes: ["variants.sizes.16chfr", "variants.sizes.18chfr"],
    sizeImages: {
      "variants.sizes.16chfr":
        "Катетер Фолея 2-х ход 16 CHFR (2 way 30 ml) Kdm medical (2)",
      "variants.sizes.18chfr":
        "Катетер Фолея 2-х ход 18 CHFR (2 way 30 ml) Kdm medical (2)",
    },
    price: 22000,
  },
  {
    id: 33,
    vat_percent: 12,
    category: "surgery",
    nameKey: "items.33.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.33.description",
    imageBases: [
      "Катетер Фолея 2-х ход 14 CHFR (2 way 30 ml) ltd kitay (2)",
      "Катетер Фолея 2-х ход 18 CHFR (2 way 30 ml) ltd kitay",
    ],
    sizes: [
      "variants.sizes.14chfr",
      "variants.sizes.16chfr",
      "variants.sizes.18chfr",
    ],
    sizeImages: {
      "variants.sizes.14chfr":
        "Катетер Фолея 2-х ход 14 CHFR (2 way 30 ml) ltd kitay (2)",
      "variants.sizes.18chfr":
        "Катетер Фолея 2-х ход 18 CHFR (2 way 30 ml) ltd kitay",
    },
    price: 8100,
  },
  {
    id: 34,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.34.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.34.description",
    imageBases: ["нелатон"],
    sizes: [
      "variants.sizes.10chfr",
      "variants.sizes.20chfr",
      "variants.sizes.22chfr",
    ],
    price: 3584,
  },
  {
    id: 35,
    vat_percent: 12,
    category: "surgery",
    nameKey: "items.35.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.35.description",
    imageBases: ["Мочеприемник 2000мл", "Мочеприемник 2000мл-2"],
    price: 7280,
  },
  {
    id: 36,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.36.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.36.description",
    imageBase: "урогенитальный",
    price: 4800,
  },
  {
    id: 37,
    vat_percent: 12,
    category: "surgery",
    nameKey: "items.37.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.37.description",
    imageBases: [
      "Трубка эндотрахеальная MEDIK PLAST размеры от  3,0 мм12 ChF",
      "Трубка эндотрахеальная MEDIK PLAST размеры  3,5 мм14 ChFr",
      "Трубка эндотрахеальная MEDIK PLAST размеры  4.5мм18 ChFr",
      "Трубка эндотрахеальная MEDIK PLAST размеры 5.0 mm 20ChFr",
      "Трубка эндотрахеальная размер 6,5 мм 26 ChFr",
      "Трубка эндотрахеальная размер 7,0 мм 28 ChFr",
      "Трубка эндотрахеальная MEDIK PLAST размеры   7,5 мм 30ChFr",
    ],
    sizes: [
      "variants.sizes.3mm",
      "variants.sizes.3p5mm",
      "variants.sizes.4mm",
      "variants.sizes.4p5mm",
      "variants.sizes.5mm",
      "variants.sizes.5p5mm",
      "variants.sizes.6mm",
      "variants.sizes.6p5mm",
      "variants.sizes.7mm",
      "variants.sizes.7p5mm",
      "variants.sizes.8mm",
      "variants.sizes.8p5mm",
    ],
    sizeImages: {
      "variants.sizes.3mm":
        "Трубка эндотрахеальная MEDIK PLAST размеры от  3,0 мм12 ChF",
      "variants.sizes.3p5mm":
        "Трубка эндотрахеальная MEDIK PLAST размеры  3,5 мм14 ChFr",
      "variants.sizes.4p5mm":
        "Трубка эндотрахеальная MEDIK PLAST размеры  4.5мм18 ChFr",
      "variants.sizes.5mm":
        "Трубка эндотрахеальная MEDIK PLAST размеры 5.0 mm 20ChFr",
      "variants.sizes.6p5mm": "Трубка эндотрахеальная размер 6,5 мм 26 ChFr",
      "variants.sizes.7mm": "Трубка эндотрахеальная размер 7,0 мм 28 ChFr",
      "variants.sizes.7p5mm":
        "Трубка эндотрахеальная MEDIK PLAST размеры   7,5 мм 30ChFr",
    },
    price: 13843,
  },
  {
    id: 38,
    vat_percent: 12,
    category: "surgery",
    nameKey: "items.38.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.38.description",
    imageBases: [
      "Трубка эндотрахеальная MEDIK PLAST размеры от  3,0 мм12 ChF",
      "Трубка эндотрахеальная MEDIK PLAST размеры  3,5 мм14 ChFr",
      "Трубка эндотрахеальная MEDIK PLAST размеры  4.5мм18 ChFr",
      "Трубка эндотрахеальная MEDIK PLAST размеры 5.0 mm 20ChFr",
      "Трубка эндотрахеальная размер 6,5 мм 26 ChFr",
      "Трубка эндотрахеальная размер 7,0 мм 28 ChFr",
      "Трубка эндотрахеальная MEDIK PLAST размеры   7,5 мм 30ChFr",
    ],
    sizeImages: {
      "variants.sizes.3mm":
        "Трубка эндотрахеальная MEDIK PLAST размеры от  3,0 мм12 ChF",
      "variants.sizes.3p5mm":
        "Трубка эндотрахеальная MEDIK PLAST размеры  3,5 мм14 ChFr",
      "variants.sizes.4p5mm":
        "Трубка эндотрахеальная MEDIK PLAST размеры  4.5мм18 ChFr",
      "variants.sizes.5mm":
        "Трубка эндотрахеальная MEDIK PLAST размеры 5.0 mm 20ChFr",
      "variants.sizes.6p5mm": "Трубка эндотрахеальная размер 6,5 мм 26 ChFr",
      "variants.sizes.7mm": "Трубка эндотрахеальная размер 7,0 мм 28 ChFr",
      "variants.sizes.7p5mm":
        "Трубка эндотрахеальная MEDIK PLAST размеры   7,5 мм 30ChFr",
    },
    sizes: [
      "variants.sizes.3mm",
      "variants.sizes.3p5mm",
      "variants.sizes.4mm",
      "variants.sizes.4p5mm",
      "variants.sizes.5mm",
      "variants.sizes.5p5mm",
      "variants.sizes.6mm",
      "variants.sizes.6p5mm",
      "variants.sizes.7mm",
      "variants.sizes.7p5mm",
      "variants.sizes.8mm",
      "variants.sizes.8p5mm",
    ],
    price: 13843,
  },
  {
    id: 39,
    vat_percent: 12,
    category: "surgery",
    nameKey: "items.39.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.39.description",
    imageBases: [
      "Зонд желудочный, стер-й размер26",
      "Зонд желудочный, стер-й размер32",
      "Зонд желудочный, стер-й размер34",
    ],
    sizes: [
      "variants.sizes.6",
      "variants.sizes.8",
      "variants.sizes.10",
      "variants.sizes.12",
      "variants.sizes.14",
      "variants.sizes.16",
      "variants.sizes.18",
    ],
    price: 6500,
  },
  {
    id: 40,
    vat_percent: 12,
    category: "surgery",
    nameKey: "items.40.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.40.description",
    imageBases: [
      "Зонд желудочный питательный назогастральный FR 12 (Medikplast)",
      "Зонд желудочный питательный назогастральный FR 14 (Medikplast)",
      "Зонд желудочный питательный назогастральный FR 16 (Medikplast)",
      "Зонд желудочный питательный назогастральный FR 18 (Medikplast)",
    ],
    sizes: [
      "variants.sizes.12fr",
      "variants.sizes.14fr",
      "variants.sizes.16fr",
      "variants.sizes.18fr",
    ],
    sizeImages: {
      "variants.sizes.12fr":
        "Зонд желудочный питательный назогастральный FR 12 (Medikplast)",
      "variants.sizes.14fr":
        "Зонд желудочный питательный назогастральный FR 14 (Medikplast)",
      "variants.sizes.16fr":
        "Зонд желудочный питательный назогастральный FR 16 (Medikplast)",
      "variants.sizes.18fr":
        "Зонд желудочный питательный назогастральный FR 18 (Medikplast)",
    },
    price: 6500,
  },
  {
    id: 41,
    vat_percent: 12,
    category: "surgery",
    nameKey: "items.41.name",
    ikpuCode: "07117001021000000",
    package_code: "1156265",
    descriptionKey: "items.41.description",
    imageBase: "зажим пупочный",
    price: 2500,
  },
  {
    id: 42,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.42.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.42.description",
    imageBases: [
      "шпатель эйра одноразовый стерильный-1",
      "шпатель эйра одноразовый стерильный",
    ],
    price: 3780,
  },
  {
    id: 43,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.43.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.43.description",
    imageBases: [
      "зонд универсальный цитошетка-2",
      "зонд универсальный цитошетка",
    ],
    price: 5040,
  },
  {
    id: 44,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.44.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.44.description",
    imageBase:
      "зеркало гинекологическое типа куско размер L ochilgani healthline",
    sizes: ["variants.sizes.m", "variants.sizes.l"],
    price: 3300,
  },
  {
    id: 45,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.45.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.45.description",
    imageBase: "зеркало гинекологическое типа куско размер M ochilgani UKM",
    sizes: ["variants.sizes.m", "variants.sizes.l"],
    price: 4350,
  },
  {
    id: 46,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.46.name",
    ikpuCode: "09018001229000000",
    package_code: "1460304",
    descriptionKey: "items.46.description",
    imageBases: [
      "набор гинекологический размер M ochilgani",
      "набор гинекологический размер L ochilgani",
    ],
    sizes: ["variants.sizes.m", "variants.sizes.l"],
    sizeImages: {
      "variants.sizes.m": "набор гинекологический размер M ochilgani",
      "variants.sizes.l": "набор гинекологический размер L ochilgani",
    },
    price: 17600,
  },
  {
    id: 47,
    vat_percent: 12,
    category: "surgery",
    nameKey: "items.47.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.47.description",
    imageBases: [
      "Лезвие скальпеля размер №10",
      "Лезвие скальпеля размер №11",
      "Лезвие скальпеля размер №12",
      "Лезвие скальпеля размер №20",
      "Лезвие скальпеля размер №21",
    ],
    sizes: [
      "variants.sizes.10",
      "variants.sizes.11",
      "variants.sizes.12",
      "variants.sizes.15",
      "variants.sizes.20",
      "variants.sizes.21",
      "variants.sizes.22",
      "variants.sizes.23",
      "variants.sizes.24",
    ],
    sizeImages: {
      "variants.sizes.10": "Лезвие скальпеля размер №10",
      "variants.sizes.11": "Лезвие скальпеля размер №11",
      "variants.sizes.12": "Лезвие скальпеля размер №12",
      "variants.sizes.20": "Лезвие скальпеля размер №20",
      "variants.sizes.21": "Лезвие скальпеля размер №21",
    },
    price: 1380,
  },
  {
    id: 48,
    vat_percent: 12,
    category: "surgery",
    nameKey: "items.48.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.48.description",
    sizes: [
      "variants.sizes.10",
      "variants.sizes.11",
      "variants.sizes.12",
      "variants.sizes.15",
      "variants.sizes.20",
      "variants.sizes.21",
      "variants.sizes.22",
      "variants.sizes.23",
      "variants.sizes.24",
    ],
    imageBases: [
      "Одноразовый скальпель размер №10",
      "Одноразовый скальпель размер №11",
      "Одноразовый скальпель размер №20 (2)",
      "Одноразовый скальпель размер №21 (2)",
      "Одноразовый скальпель размер №24 (2)",
    ],
    sizeImages: {
      "variants.sizes.10": "Одноразовый скальпель размер №10",
      "variants.sizes.11": "Одноразовый скальпель размер №11",
      "variants.sizes.20": "Одноразовый скальпель размер №20 (2)",
      "variants.sizes.21": "Одноразовый скальпель размер №21 (2)",
      "variants.sizes.24": "Одноразовый скальпель размер №24 (2)",
    },
    price: 3700,
  },
  {
    id: 49,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.49.name",
    ikpuCode: "04014001007000000",
    package_code: "1342698",
    descriptionKey: "items.49.description",
    imageBase: "Лейкопластырь (72см19м)  ONE AID №500",
    price: 30000,
  },
  {
    id: 50,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.50.name",
    ikpuCode: "04014001007000000",
    package_code: "1342698",
    descriptionKey: "items.50.description",
    imageBase: "Лейкопластырь (72см19м)  ONE AID №500",
    price: 115000,
  },
  {
    id: 51,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.51.name",
    ikpuCode: "04014001007000000",
    package_code: "1342698",
    descriptionKey: "items.51.description",
    imageBase: "Лейкопластырь (72см19м) ДЕТСКИЙ ONE AID №20 (2)",
    price: 10600,
  },
  {
    id: 52,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.52.name",
    ikpuCode: "04014001007000000",
    package_code: "1342698",
    descriptionKey: "items.52.description",
    imageBase: "мозольный",
    price: 9400,
  },
  {
    id: 53,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.53.name",
    ikpuCode: "04014001007000000",
    package_code: "1342698",
    imageBases: [
      "Пластырь на основе полиэтилена 1,25см500см (2)",
      "Пластырь на основе полиэтилена 2,5см500см",
    ],
    sizes: ["variants.sizes.1p25", "variants.sizes.2p5"],
    sizeImages: {
      "variants.sizes.1p25": "Пластырь на основе полиэтилена 1,25см500см (2)",
      "variants.sizes.2p5": "Пластырь на основе полиэтилена 2,5см500см",
    },
    sizesDescription: {
      "variants.sizes.1p25": "items.53.description.1p25",
      "variants.sizes.2p5": "items.53.description.2p5",
    },
    sizePrices: {
      "variants.sizes.1p25": 2900,
      "variants.sizes.2p5": 5800,
    },
  },
  {
    id: 54,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.54.name",
    ikpuCode: "04014001007000000",
    package_code: "1342698",
    imageBases: [
      "Пластырь на нетканой основе 10см х 15см",
      "Пластырь на нетканой основе 10см х 20см (3)",
      "Пластырь на нетканой основе 10см х 30см (2)",
    ],
    sizes: [
      "variants.sizes.10b15",
      "variants.sizes.10b20",
      "variants.sizes.10b30",
    ],
    sizeImages: {
      "variants.sizes.10b15": "Пластырь на нетканой основе 10см х 15см",
      "variants.sizes.10b20": "Пластырь на нетканой основе 10см х 20см (3)",
      "variants.sizes.10b30": "Пластырь на нетканой основе 10см х 30см (2)",
    },
    sizesDescription: {
      "variants.sizes.10b15": "items.54.description.10b15",
      "variants.sizes.10b20": "items.54.description.10b20",
      "variants.sizes.10b30": "items.54.description.10b30",
    },
    sizePrices: {
      "variants.sizes.10b15": 5800,
      "variants.sizes.10b20": 8500,
      "variants.sizes.10b30": 12500,
    },
  },
  {
    id: 55,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.55.name",
    ikpuCode: "04014001007000000",
    package_code: "1342698",
    descriptionKey: "items.55.description",
    imageBases: [
      "Пластырь MEDIK PLAST 2,5см х 10м №1 (2)",
      "Пластырь MEDIK PLAST 5см х 10м №1 (3)",
      "Пластырь MEDIK PLAST 10см х 10м №1 (3)",
      "Пластырь MEDIK PLAST 15см х 10м №1 (2)",
    ],
    sizes: [
      "variants.sizes.2p5b10m",
      "variants.sizes.5b10m",
      "variants.sizes.10b10m",
      "variants.sizes.15b10m",
    ],
    sizeImages: {
      "variants.sizes.5b10m": "Пластырь MEDIK PLAST 5см х 10м №1 (3)",
      "variants.sizes.10b10m": "Пластырь MEDIK PLAST 10см х 10м №1 (3)",
      "variants.sizes.15b10m": "Пластырь MEDIK PLAST 15см х 10м №1 (2)",
    },
    sizePrices: {
      "variants.sizes.2p5b10m": 45000,
      "variants.sizes.5b10m": 70000,
      "variants.sizes.10b10m": 110000,
      "variants.sizes.15b10m": 137000,
    },
  },
  {
    id: 56,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.56.name",
    ikpuCode: "03005001003010007",
    package_code: "1206896",
    descriptionKey: "items.56.description",
    imageBases: [
      "Бинт марлевый стерилный 5м10см",
      "Бинт марлевый стерильный 7м14см",
    ],
    sizes: ["variants.sizes.5b10", "variants.sizes.7b14"],
    sizeImages: {
      "variants.sizes.5b10": "Бинт марлевый стерилный 5м10см",
      "variants.sizes.7b14": "Бинт марлевый стерильный 7м14см",
    },
    sizePrices: {
      "variants.sizes.5b10": 2300,
      "variants.sizes.7b14": 4726,
    },
    sizesDescription: {
      "variants.sizes.5b10": "items.56.description.5m",
      "variants.sizes.7b14": "items.56.description.7m",
    },
  },
  {
    id: 57,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.57.name",
    descriptionKey: "items.57.description",
    sizeIkpuCodes: {
      "variants.sizes.5b10": "03005001003010005",
      "variants.sizes.7b14": "03005001003010001",
    },
    sizePackageCodes: {
      "variants.sizes.5b10": "1206894",
      "variants.sizes.7b14": "1206890",
    },
    imageBases: [
      "Бинт марлевый НС 5м10см SARBONTEKS",
      "Бинт марлевый нс 7мx14см SARBONTEKS",
    ],
    sizes: ["variants.sizes.5b10", "variants.sizes.7b14"],
    sizeImages: {
      "variants.sizes.5b10": "Бинт марлевый НС 5м10см SARBONTEKS",
      "variants.sizes.7b14": "Бинт марлевый нс 7мx14см SARBONTEKS",
    },
    sizePrices: {
      "variants.sizes.5b10": 1790,
      "variants.sizes.7b14": 2464,
    },
    sizesDescription: {
      "variants.sizes.5b10": "items.57.description.5m",
      "variants.sizes.7b14": "items.57.description.7m",
    },
  },
  {
    id: 58,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.58.name",
    ikpuCode: "03005001003010023",
    package_code: "1206912",
    descriptionKey: "items.58.description",
    imageBase: "Бинт тканый НС 7м14см SARBONTEKS",
    price: 3900,
  },
  {
    id: 59,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.59.name",
    ikpuCode: "03005001003010007",
    package_code: "1206896",
    descriptionKey: "items.59.description",
    imageBase: "Бинт тканый СТЕРИЛЬНЫЙ тип 50м 7м14см САРБОНТЕКС",
    price: 5240,
  },
  {
    id: 60,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.60.name",
    sizeIkpuCodes: {
      "variants.sizes.5b10": "03005001003010005",
      "variants.sizes.7b14": "03005001003010001",
    },
    sizePackageCodes: {
      "variants.sizes.5b10": "1206894",
      "variants.sizes.7b14": "1206890",
    },
    descriptionKey: "items.60.description",
    imageBases: ["Бинт марлевый  нс 5м10см (2)", "Бинт марлевый нс 7мх14см"],
    sizes: ["variants.sizes.5b10", "variants.sizes.7b14"],
    sizeImages: {
      "variants.sizes.5b10": "Бинт марлевый  нс 5м10см (2)",
      "variants.sizes.7b14": "Бинт марлевый нс 7мх14см",
    },
    sizePrices: {
      "variants.sizes.5b10": 1850,
      "variants.sizes.7b14": 2975,
    },
  },
  {
    id: 61,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.61.name",
    ikpuCode: "03005001003010023",
    package_code: "1206912",
    descriptionKey: "items.61.description",
    imageBases: ["Бинт тканый нс 5м10см", "Бинт тканый нс 7м14см"],
    sizes: ["variants.sizes.5b10", "variants.sizes.7b14"],
    sizeImages: {
      "variants.sizes.5b10": "Бинт тканый нс 5м10см",
      "variants.sizes.7b14": "Бинт тканый нс 7м14см",
    },
    sizePrices: {
      "variants.sizes.5b10": 3472,
      "variants.sizes.7b14": 3780,
    },
    sizesDescription: {
      "variants.sizes.5b10": "items.61.description.5m",
      "variants.sizes.7b14": "items.61.description.7m",
    },
  },
  {
    id: 62,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.62.name",
    ikpuCode: "03005001003010007",
    package_code: "1206896",
    descriptionKey: "items.62.description",
    imageBase: "Бинт тканый СТЕРИЛЬНЫЙ тип 50м 7м14см",
    price: 4650,
  },
  {
    id: 63,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.63.name",
    ikpuCode: "03005001003000000",
    package_code: "1196884",
    descriptionKey: "items.63.description",
    imageBase: "Бинт гипсовый 2,7м х 15см",
    price: 8800,
  },
  {
    id: 64,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.64.name",
    ikpuCode: "05601001001000000",
    package_code: "1390927",
    descriptionKey: "items.64.description",
    imageBases: [
      "Вата нс 25г.",
      "Вата нс 50г.",
      "Вата нс 100г.",
      "Вата нс 250г.",
    ],
    sizes: [
      "variants.sizes.25gr",
      "variants.sizes.50gr",
      "variants.sizes.100gr",
      "variants.sizes.250gr",
    ],
    sizeImages: {
      "variants.sizes.25gr": "Вата нс 25г.",
      "variants.sizes.50gr": "Вата нс 50г.",
      "variants.sizes.100gr": "Вата нс 100г.",
      "variants.sizes.250gr": "Вата нс 250г.",
    },
    sizePrices: {
      "variants.sizes.25gr": 1717,
      "variants.sizes.50gr": 3292,
      "variants.sizes.100gr": 6000,
      "variants.sizes.250gr": 14308,
    },
    sizesDescription: {
      "variants.sizes.25gr": "items.64.description.25gr",
      "variants.sizes.50gr": "items.64.description.50gr",
      "variants.sizes.100gr": "items.64.description.100gr",
      "variants.sizes.250gr": "items.64.description.250gr",
    },
  },
  {
    id: 65,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.65.name",
    ikpuCode: "05601001001000000",
    package_code: "1390927",
    descriptionKey: "items.65.description",
    imageBases: ["sarbonteks_вата"],
    sizes: [
      "variants.sizes.25gr",
      "variants.sizes.50gr",
      "variants.sizes.100gr",
      "variants.sizes.250gr",
    ],
    sizePrices: {
      "variants.sizes.25gr": 2200,
      "variants.sizes.50gr": 4300,
      "variants.sizes.100gr": 8200,
      "variants.sizes.250gr": 19500,
    },
  },
  {
    id: 66,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.66.name",
    ikpuCode: "05601001001000000",
    package_code: "1390927",
    descriptionKey: "items.66.description",
    imageBase: "vata_back_enhanced",
    sizes: ["variants.sizes.25gr", "variants.sizes.50gr"],
    sizePrices: {
      "variants.sizes.25gr": 2430,
      "variants.sizes.50gr": 4290,
    },
  },
  {
    id: 67,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.67.name",
    ikpuCode: "05601001001000000",
    package_code: "1390927",
    descriptionKey: "items.67.description",
    imageBases: [
      "Вата Cotton club 25 гр (рулон)",
      "Вата Cotton club 25 гр непресс",
      "Вата Cotton club 50 гр (рулон)",
      "Вата Cotton club 50 гр (Шарики)",
      "Вата Cotton club 100 гр (рулон)",
      "Вата Cotton club 250 гр (рулон)",
    ],
    sizes: [
      "variants.sizes.25gr",
      "variants.sizes.25grnonroll",
      "variants.sizes.50gr",
      "variants.sizes.50grBall",
      "variants.sizes.100gr",
      "variants.sizes.250gr",
    ],
    sizeImages: {
      "variants.sizes.25g": "Вата Cotton club 25 гр (рулон)",
      "variants.sizes.25gNonroll": "Вата Cotton club 25 гр непресс",
      "variants.sizes.50g": "Вата Cotton club 50 гр (рулон)",
      "variants.sizes.50gBall": "Вата Cotton club 50 гр (Шарики)",
      "variants.sizes.100g": "Вата Cotton club 100 гр (рулон)",
      "variants.sizes.250g": "Вата Cotton club 250 гр (рулон)",
    },
    sizePrices: {
      "variants.sizes.25g": 2300,
      "variants.sizes.25gNonroll": 2300,
      "variants.sizes.50g": 4400,
      "variants.sizes.50gBall": 6200,
      "variants.sizes.100g": 8500,
      "variants.sizes.250g": 17900,
    },
    sizesDescription: {
      "variants.sizes.25g": "items.67.description.25gRoll",
      "variants.sizes.25gNonroll": "items.67.description.25g",
      "variants.sizes.50g": "items.67.description.50g",
      "variants.sizes.50gBall": "items.67.description.50gBall",
      "variants.sizes.100g": "items.67.description.100gRoll",
      "variants.sizes.250g": "items.67.description.250gRoll",
    },
  },
  {
    id: 68,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.68.name",
    sizeIkpuCodes: {
      "variants.sizes.1b90": "03005001002004001",
      "variants.sizes.2b90": "03005001002004002",
      "variants.sizes.3b90": "03005001002004003",
      "variants.sizes.5b84": "03005001002004004",
      "variants.sizes.5b90": "03005001002004004",
      "variants.sizes.10b90": "03005001002004005",
    },
    sizePackageCodes: {
      "variants.sizes.1b90": "1206755",
      "variants.sizes.2b90": "1206755",
      "variants.sizes.3b90": "1206757",
      "variants.sizes.5b84": "1206758",
      "variants.sizes.5b90": "1206758",
      "variants.sizes.10b90": "1206759",
    },
    descriptionKey: "items.68.description",
    imageBases: [
      "Марля в кусках 1мх90см.",
      "Марля в кусках 2мх90см.",
      "Марля в кусках 3мх90см.",
      "Марля  в кусках 5мх84см.",
      "Марля  в кусках 5мх90см.",
      "Марля в кусках 10мх90см.",
    ],
    sizes: [
      "variants.sizes.1b90",
      "variants.sizes.2b90",
      "variants.sizes.3b90",
      "variants.sizes.5b84",
      "variants.sizes.5b90",
      "variants.sizes.10b90",
    ],
    sizeImages: {
      "variants.sizes.1b90": "Марля в кусках 1мх90см.",
      "variants.sizes.2b90": "Марля в кусках 2мх90см.",
      "variants.sizes.3b90": "Марля в кусках 3мх90см.",
      "variants.sizes.5b84": "Марля  в кусках 5мх84см.",
      "variants.sizes.5b90": "Марля  в кусках 5мх90см.",
      "variants.sizes.10b90": "Марля в кусках 10мх90см.",
    },
    sizePrices: {
      "variants.sizes.1b90": 2980,
      "variants.sizes.2b90": 6200,
      "variants.sizes.3b90": 8900,
      "variants.sizes.5b84": 17500,
      "variants.sizes.5b90": 14800,
      "variants.sizes.10b90": 27000,
    },
    sizesDescription: {
      "variants.sizes.1b90": "items.68.description.1b90",
      "variants.sizes.2b90": "items.68.description.2b90",
      "variants.sizes.3b90": "items.68.description.3b90",
      "variants.sizes.5b84": "items.68.description.5b84",
      "variants.sizes.5b90": "items.68.description.5b90",
      "variants.sizes.10b90": "items.68.description.10b90",
    },
  },
  {
    id: 69,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.69.name",
    sizeIkpuCodes: {
      "variants.sizes.1b90": "03005001002004001",
      "variants.sizes.2b90": "03005001002004002",
      "variants.sizes.3b90": "03005001002004003",
      "variants.sizes.5b90": "03005001002004004",
      "variants.sizes.10b90": "03005001002004005",
    },
    sizePackageCodes: {
      "variants.sizes.1b90": "1206755",
      "variants.sizes.2b90": "1206755",
      "variants.sizes.3b90": "1206757",
      "variants.sizes.5b90": "1206758",
      "variants.sizes.10b90": "1206759",
    },
    descriptionKey: "items.69.description",
    imageBases: [
      "Марля в кусках 1мх90см. SARBONTEKS",
      "Марля в кусках 2мх90см. SARBONTEKS",
      "Марля в кусках 3мх90см. SARBONTEKS",
      "Марля в кусках 5мх90см. SARBONTEKS",
      "Марля в кусках 10мх90см SARBONTEKS",
    ],
    sizes: [
      "variants.sizes.1b90",
      "variants.sizes.2b90",
      "variants.sizes.3b90",
      "variants.sizes.5b90",
      "variants.sizes.10b90",
    ],
    sizeImages: {
      "variants.sizes.1b90": "Марля в кусках 1мх90см. SARBONTEKS",
      "variants.sizes.2b90": "Марля в кусках 2мх90см. SARBONTEKS",
      "variants.sizes.3b90": "Марля в кусках 3мх90см. SARBONTEKS",
      "variants.sizes.5b90": "Марля в кусках 5мх90см. SARBONTEKS",
      "variants.sizes.10b90": "Марля в кусках 10мх90см SARBONTEKS",
    },
    sizePrices: {
      "variants.sizes.1b90": 2750,
      "variants.sizes.2b90": 5500,
      "variants.sizes.3b90": 8200,
      "variants.sizes.5b90": 14000,
      "variants.sizes.10b90": 27000,
    },
    sizesDescription: {
      "variants.sizes.1b90": "items.69.description.1b90",
      "variants.sizes.2b90": "items.69.description.2b90",
      "variants.sizes.3b90": "items.69.description.3b90",
      "variants.sizes.5b90": "items.69.description.5b90",
      "variants.sizes.10b90": "items.69.description.10b90",
    },
  },
  {
    id: 70,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.70.name",
    sizeIkpuCodes: {
      "variants.sizes.5b90": "03005001002004004",
      "variants.sizes.10b90": "03005001002004005",
    },
    sizePackageCodes: {
      "variants.sizes.5b90": "1206758",
      "variants.sizes.10b90": "1206759",
    },
    descriptionKey: "items.70.description",
    imageBases: [
      "Марля в кусках 5мх90см. По ГОСТу SARBONTEKS",
      "Марля в кусках 10мх90см. По ГОСТу SARBONTEKS",
    ],
    sizes: ["variants.sizes.5b90", "variants.sizes.10b90"],
    sizeImages: {
      "variants.sizes.5b90": "Марля в кусках 5мх90см. По ГОСТу SARBONTEKS",
      "variants.sizes.10b90": "Марля в кусках 10мх90см. По ГОСТу SARBONTEKS",
    },
    sizePrices: {
      "variants.sizes.5b90": 19800,
      "variants.sizes.10b90": 39500,
    },
    sizesDescription: {
      "variants.sizes.5b90": "items.70.description.5b90",
      "variants.sizes.10b90": "items.70.description.10b90",
    },
  },
  {
    id: 71,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.71.name",
    ikpuCode: "03005001006000000",
    package_code: "1196881",
    descriptionKey: "items.71.description",
    imageBases: [
      "Салфетки марлевые стерил. 2-х сл. 16х14 №10",
      "Салфетки марлевые стерил. 2-х сл. 45х29 №10",
    ],
    sizes: ["variants.sizes.16b14", "variants.sizes.45b29"],
    sizeImages: {
      "variants.sizes.16b14": "Салфетки марлевые стерил. 2-х сл. 16х14 №10",
      "variants.sizes.45b29": "Салфетки марлевые стерил. 2-х сл. 45х29 №10",
    },
    sizePrices: {
      "variants.sizes.16b14": 3700,
      "variants.sizes.45b29": 5200,
    },
    sizesDescription: {
      "variants.sizes.16b14": "items.71.description.16b14",
      "variants.sizes.45b29": "items.71.description.45b29",
    },
  },
  {
    id: 72,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.72.name",
    ikpuCode: "03005001006000000",
    package_code: "1196881",
    descriptionKey: "items.72.description",
    imageBases: [
      "Салфетки стерил 16см х 14см №-10 SARBON TEKS",
      "Салфетки стерил 45см х 29см №-10 SARBON TEKS",
    ],
    sizes: ["variants.sizes.16b14", "variants.sizes.45b29"],
    sizeImages: {
      "variants.sizes.16b14": "Салфетки стерил 16см х 14см №-10 SARBON TEKS",
      "variants.sizes.45b29": "Салфетки стерил 45см х 29см №-10 SARBON TEKS",
    },
    sizePrices: {
      "variants.sizes.16b14": 3800,
      "variants.sizes.45b29": 4592,
    },
    sizesDescription: {
      "variants.sizes.16b14": "items.72.description.16b14",
      "variants.sizes.45b29": "items.72.description.45b29",
    },
  },
  {
    id: 73,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.73.name",
    ikpuCode: "03926001005000000",
    package_code: "1520328",
    descriptionKey: "items.73.description",
    imageBase: "Бахилы одноразовые из тканый материал",
    price: 450,
  },
  {
    id: 74,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.74.name",
    ikpuCode: "03926001005000000",
    package_code: "1520328",
    descriptionKey: "items.74.description",
    imageBase: "Бахилы одноразовые из тканый материал",
    price: 225000,
  },
  {
    id: 75,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.75.name",
    ikpuCode: "06506001006000000",
    package_code: "1321737",
    descriptionKey: "items.75.description",
    imageBases: ["Шапочки одноразовые цвет белий"],
    colors: ["variants.colors.white", "variants.colors.green"],
    price: 690,
  },
  {
    id: 76,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.76.name",
    ikpuCode: "06506001006000000",
    package_code: "1321737",
    descriptionKey: "items.76.description",
    imageBases: [
      "Шапочки одноразовые цвет белий",
      "Шапочки одноразовые цвет роз",
    ],
    colors: ["variants.colors.white", "variants.colors.pink"],
    colorImages: {
      "variants.colors.white": "Шапочки одноразовые цвет белий",
      "variants.colors.pink": "Шапочки одноразовые цвет роз",
    },
    price: 690,
  },
  {
    id: 77,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.77.name",
    ikpuCode: "06506001006000000",
    package_code: "1321737",
    descriptionKey: "items.77.description",
    imageBase: "shapka-bahila",
    price: 600,
  },
  {
    id: 78,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.78.name",
    ikpuCode: "06506001006000000",
    package_code: "1321737",
    descriptionKey: "items.78.description",
    imageBase: "Шапочки полиэтилен Белый новый",
    price: 500,
  },
  {
    id: 79,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.79.name",
    ikpuCode: "06506001006000000",
    package_code: "1321737",
    descriptionKey: "items.79.description",
    imageBases: [
      "Шапочки полиэтилен Белый новый",
      "Шапочки одноразовые цвет черный",
    ],
    colors: ["variants.colors.white", "variants.colors.black"],
    colorImages: {
      "variants.colors.white": "Шапочки полиэтилен Белый новый",
      "variants.colors.black": "Шапочки одноразовые цвет черный",
    },
    price: 650,
  },
  {
    id: 80,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.80.name",
    ikpuCode: "09018001229000000",
    package_code: "1460304",
    descriptionKey: "items.80.description",
    imageBase: "олива для промывания носа",
    price: 1456,
  },
  {
    id: 81,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.81.name",
    ikpuCode: "09018001229000000", 
    package_code: "1460304",
    descriptionKey: "items.81.description",
    imageBase: "воронки ушные одноразовые-1",
    price: 1456,
  },
  {
    id: 82,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.82.name",
    ikpuCode: "09018001229000000", 
    package_code: "1460304",
    descriptionKey: "items.82.description",
    imageBase: "канюля для промывания миндалин",
    price: 1792,
  },
  {
    id: 83,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.83.name",
    ikpuCode: "09018001229000000", 
    package_code: "1460304",
    descriptionKey: "items.83.description",
    imageBase: "зонд тампон для отбора проб из носаносоглотки",
    price: 1490,
  },
  {
    id: 84,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.84.name",
    ikpuCode: "09018001229000000", 
    package_code: "1460304",
    descriptionKey: "items.84.description",
    imageBase: "шпатель лор одноразовый пластиковый-2",
    price: 750,
  },
  {
    id: 85,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.85.name",
    ikpuCode: "09018001229000000", 
    package_code: "1460304",
    descriptionKey: "items.85.description",
    imageBase: "слюноотсосSE-01 SalivaEjectors 100-1",
    price: 59000,
  },
  {
    id: 86,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.86.name",
    descriptionKey: "items.86.description",
    imageBase: "набор стомалогический Nest dent-2",
    price: 16700,
  },
  {
    id: 87,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.87.name",
    ikpuCode: "09018001229000000", 
    package_code: "1460304",
    descriptionKey: "items.87.description",
    imageBase: "ватный валик 10x38mm 600 шт",
    sizes: ["variants.sizes.600pc", "variants.sizes.1000pc"],
    sizePrices: {
      "variants.sizes.600pc": 77000,
      "variants.sizes.1000pc": 115000,
    },
  },
  {
    id: 88,
    vat_percent: 12,
    category: "surgery",
    nameKey: "items.88.name",
    ikpuCode: "06214001007000000",
    package_code: "1190254",
    descriptionKey: "items.88.description",
    imageBase: "Хирургический комплект стерильный 35гр",
    sizes: ["variants.sizes.35g", "variants.sizes.45g"],
    sizesDescription: {
      "variants.sizes.35g": "items.88.description.35g",
      "variants.sizes.45g": "items.88.description.45g",
    },
    sizePrices: {
      "variants.sizes.35g": 36000,
      "variants.sizes.45g": 40000,
    },
  },
  {
    id: 89,
    vat_percent: 12,
    category: "surgery",
    nameKey: "items.89.name",
    ikpuCode: "06214001007000000",
    package_code: "1190254",
    descriptionKey: "items.89.description",
    imageBase: "Хирургический комплект стерильный 35гр",
    price: 30000,
  },
  {
    id: 90,
    vat_percent: 12,
    category: "surgery",
    nameKey: "items.90.name",
    ikpuCode: "06214001007000000",
    package_code: "1190254",
    descriptionKey: "items.90.description",
    imageBases: ["Хирургический халат стерильный (25 грм2) ukm"],
    price: 22000
  },
  {
    id: 91,
    vat_percent: 12,
    category: "surgery",
    nameKey: "items.91.name",
    ikpuCode: "06214001007000000",
    package_code: "1190254",
    descriptionKey: "items.91.description",
    imageBase: "хирургический Халат 37 грм2",
    price: 29000,
  },
  {
    id: 92,
    vat_percent: 12,
    category: "surgery",
    nameKey: "items.92.name",
    ikpuCode: "06214001007000000",
    package_code: "1190254",
    descriptionKey: "items.92.description",
    imageBases: [
      "Хирургический халат стерильный (25 грм2) NEST-GOWN",
      "Хирургический халат стерильный (45 грм2) (2)",
    ],
    sizes: ["variants.sizes.25g", "variants.sizes.35g", "variants.sizes.45g"],
    sizeImages: {
      "variants.sizes.25g":
        "Хирургический халат стерильный (25 грм2) NEST-GOWN",
      "variants.sizes.45g": "Хирургический халат стерильный (45 грм2) (2)",
    },
    sizePrices: {
      "variants.sizes.25g": 21000,
      "variants.sizes.35g": 25000,
      "variants.sizes.45g": 30000,
    },
    sizesDescription: {
      "variants.sizes.25g": "items.92.description.25gmsq",
      "variants.sizes.35g": "items.92.description.35gmsq",
      "variants.sizes.45g": "items.92.description.45gmsq",
    },
  },
  {
    id: 93,
    vat_percent: 12,
    category: "surgery",
    nameKey: "items.93.name",
    ikpuCode: "06302001001000000", 
    package_code: "1373758",
    descriptionKey: "items.93.description",
    imageBases: [
      "Хирургическая простыня стерильный 80200-25гр UKM",
      "Хирургическая простыня стерильный 80200-35гр UKM",
    ],
    sizes: [
      "variants.sizes.25gr80b200",
      "variants.sizes.35gr160b200",
      "variants.sizes.35gr80b200",
    ],
    sizePrices: {
      "variants.sizes.25gr80b200": 11000,
      "variants.sizes.35gr160b200": 19500,
      "variants.sizes.35gr80b200": 13000,
    },
    sizesDescription: {
      "variants.sizes.25gr80b200": "items.93.description.25gr80b200",
      "variants.sizes.35gr160b200": "items.93.description.35gr160b200",
      "variants.sizes.35gr80b200": "items.93.description.35gr80b200",
    },
  },
  {
    id: 94,
    vat_percent: 12,
    category: "surgery",
    nameKey: "items.94.name",
    ikpuCode: "06302001001000000", 
    package_code: "1373758",
    descriptionKey: "items.94.description",
    imageBases: [
      "Хирург-я простыня стерильная 200х160см (25грм2) NEST-DRAPE",
      "Хирург-я простыня стерильная 200х160см (25грм2)",
      "Хирург-я простыня стерильная 200х160см (45грм2) NEST-DRAPE",
    ],
    sizes: [
      "variants.sizes.25gr200b160",
      "variants.sizes.35gr200b160",
      "variants.sizes.45gr200b160",
    ],
    sizePrices: {
      "variants.sizes.25gr200b160": 16000,
      "variants.sizes.35gr200b160": 19000,
      "variants.sizes.45gr200b160": 22000,
    },
    sizesDescription: {
      "variants.sizes.25gr200b160": "items.94.description.200b16025g",
      "variants.sizes.35gr200b160": "items.94.description.200b16035g",
      "variants.sizes.45gr200b160": "items.94.description.200b16045g",
    },
  },
  {
    id: 95,
    vat_percent: 12,
    category: "surgery",
    nameKey: "items.95.name",
    ikpuCode: "06302001001000000", 
    package_code: "1373758",
    descriptionKey: "items.95.description",
    imageBase: "Хирургическая простыня стерильный 80200-35гр UKM",
    price: 70000,
  },
  {
    id: 96,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.96.name",
    ikpuCode: "03402004013000000",
    package_code: "1476516",
    descriptionKey: "items.96.description",
    imageBase: "хлорактив",
    price: 70000,
  },
  {
    id: 97,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.97.name",
    ikpuCode: "03401004002010001",
    package_code: "1346589",
    descriptionKey: "items.97.description",
    imageBase: "Септодез 1 литр",
    price: 120000,
  },
  {
    id: 98,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.98.name",
    ikpuCode: "03808001003000006",
    package_code: "1489288",
    descriptionKey: "items.98.description",
    imageBase: "АБК Экстра 1 литр",
    price: 122000,
  },
  {
    id: 99,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.99.name",
    ikpuCode: "03304020002000000",
    package_code: "1378746",
    descriptionKey: "items.99.description",
    imageBase: "Антисептин 0,5 литр (марка А) с триггером",
    price: 58000,
  },
  {
    id: 100,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.100.name",
    ikpuCode: "03304020001000000",
    package_code: "1344125",
    descriptionKey: "items.100.description",
    imageBase: "Антисептин ОП 0,5л (марка Б) с триггером",
    price: 45500,
  },
  {
    id: 101,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.101.name",
    ikpuCode: "03808001003000004",
    package_code: "1489286",
    descriptionKey: "items.101.description",
    imageBase: "ДС 1 0,5л концентрат",
    price: 250000,
  },
  {
    id: 102,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.102.name",
    ikpuCode: "03808001003000002",
    package_code: "1489284",
    descriptionKey: "items.102.description",
    imageBase: "Экстрадин 1л (Концентрат НОВЫЙ)",
    price: 320000,
  },
  {
    id: 103,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.103.name",
    ikpuCode: "03808001003000007",
    package_code: "1489289",
    descriptionKey: "items.103.description",
    imageBase: "Экстра Дез плюс 1л (концентрат)",
    price: 375000,
  },
  {
    id: 104,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.104.name",
    ikpuCode: "02932001002000000",
    package_code: "1439293",
    descriptionKey: "items.104.description",
    imageBase: "Фенолфталеин 100мл",
    price: 15000,
  },
  {
    id: 105,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.105.name",
    ikpuCode: "03822001001000014",
    package_code: "1489324",
    descriptionKey: "items.105.description",
    imageBase: "АзопирамС-GTM (100 мл)",
    price: 44000,
  },
  {
    id: 106,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.106.name",
    ikpuCode: "09031001030000000",
    package_code: "1461323",
    descriptionKey: "items.106.description",
    imageBases: [
      "Ультразвуковой очиститель 2Л (2)",
      "Ультразвуковой очиститель 4,5Л",
    ],
    sizes: [
      "variants.sizes.2l",
      "variants.sizes.3l",
      "variants.sizes.4p5l",
      "variants.sizes.6l",
    ],
    sizeImages: {
      "variants.sizes.2l": "Ультразвуковой очиститель 2Л (2)",
      "variants.sizes.4p5l": "Ультразвуковой очиститель 4,5Л",
    },
    sizePrices: {
      "variants.sizes.2l": 1650000,
      "variants.sizes.3l": 1915000,
      "variants.sizes.4p5l": 2580000,
      "variants.sizes.6l": 3250000,
    },
    sizesDescription: {
      "variants.sizes.2l": "items.106.description.2l",
      "variants.sizes.3l": "items.106.description.3l",
      "variants.sizes.4p5l": "items.106.description.4p5l",
      "variants.sizes.6l": "items.106.description.6l",
    },
  },
  {
    id: 107,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.107.name",
    descriptionKey: "items.107.description",
    imageBases: [
      "пластиковый контейнер для утилизации 1литр (1)",
      "пластиковый контейнер для утилизации 2литр (1)",
      "пластиковый контейнер для утилизации 3литр",
      "пластиковый контейнер для утилизации 4литр",
      "пластиковый контейнер для утилизации 5литр",
    ],
    sizes: [
      "variants.sizes.1l",
      "variants.sizes.2l",
      "variants.sizes.3l",
      "variants.sizes.4l",
      "variants.sizes.5l",
    ],
    sizeImages: {
      "variants.sizes.1l": "пластиковый контейнер для утилизации 1литр (1)",
      "variants.sizes.2l": "пластиковый контейнер для утилизации 2литр (1)",
      "variants.sizes.3l": "пластиковый контейнер для утилизации 3литр",
      "variants.sizes.4l": "пластиковый контейнер для утилизации 4литр",
      "variants.sizes.5l": "пластиковый контейнер для утилизации 5литр",
    },
    sizePrices: {
      "variants.sizes.1l": 11600,
      "variants.sizes.2l": 13500,
      "variants.sizes.3l": 18500,
      "variants.sizes.4l": 24000,
      "variants.sizes.5l": 27500,
    },
    sizesDescription: {
      "variants.sizes.1l": "items.107.description.1l",
      "variants.sizes.2l": "items.107.description.2l",
      "variants.sizes.3l": "items.107.description.3l",
      "variants.sizes.4l": "items.107.description.4l",
      "variants.sizes.5l": "items.107.description.5l",
    },
  },
  {
    id: 108,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.108.name",
    ikpuCode: "03822001001000000",
    package_code: "1476764",
    descriptionKey: "items.108.description",
    imageBase: "журнал индикатора",
    price: 40000,
  },
  {
    id: 109,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.109.name",
    ikpuCode: "03822001001000000",
    package_code: "1476764",
    descriptionKey: "items.109.description",
    imageBase:
      "внутренний индикатор для паровой стерилизации 132-20-(1000шт) Unitest-steam",
    price: 276000,
  },
  {
    id: 110,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.110.name",
    ikpuCode: "03822001001000000",
    package_code: "1476764",
    descriptionKey: "items.110.description",
    imageBase:
      "наружный индикатор для паровой стерилизации 132-20-(1000шт) Unitest-steam",
    price: 101000,
  },
  {
    id: 111,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.111.name",
    ikpuCode: "03822001001000000",
    package_code: "1476764",
    descriptionKey: "items.111.description",
    imageBase:
      "универсальный индикатор для паровой стерилизации 134-5-(1000шт) Unitest-steam",
    price: 199000,
  },
  {
    id: 112,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.112.name",
    ikpuCode: "03822001001000000",
    package_code: "1476764",
    descriptionKey: "items.112.description",
    imageBase: "Индикатор стерилизации Фарма-тест 1345",
    price: 274000,
  },
  {
    id: 113,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.113.name",
    ikpuCode: "03822001001000000",
    package_code: "1476764",
    descriptionKey: "items.113.description",
    imageBase: "индикатор Стерилан уп-132-20(1000шт) ташки",
    price: 145000,
  },
  {
    id: 114,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.114.name",
    ikpuCode: "03822001001000000",
    package_code: "1476764",
    descriptionKey: "items.114.description",
    imageBase: "индикатор Стерилан уп-132-20(1000шт) ички",
    price: 290000,
  },
  {
    id: 115,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.115.name",
    ikpuCode: "03822001001000000",
    package_code: "1476764",
    descriptionKey: "items.115.description",
    imageBase: "индикатор Стерилан уп-180-60(1000шт) ташки",
    price: 145000,
  },
  {
    id: 116,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.116.name",
    ikpuCode: "03822001001000000",
    package_code: "1476764",
    descriptionKey: "items.116.description",
    imageBase: "индикатор Стерилан уп-180-60(1000шт) ички",
    price: 290000,
  },
  {
    id: 117,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.117.name",
    ikpuCode: "03822001001000000",
    package_code: "1476764",
    descriptionKey: "items.117.description",
    imageBase: "индикатор стерил Медис-В-180-60-1,1000",
    price: 148000,
  },
  {
    id: 118,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.118.name",
    ikpuCode: "03822001001000000",
    package_code: "1476764",
    descriptionKey: "items.118.description",
    imageBase: "Индикатор стерилизации Стеритест-ВЛ",
    price: 490000,
  },
  {
    id: 119,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.119.name",
    ikpuCode: "03822001001000000",
    package_code: "1476764",
    descriptionKey: "items.119.description",
    imageBase: "индикатор стерил Медис-В-132-20,1000",
    price: 148000,
  },
  {
    id: 120,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.120.name",
    ikpuCode: "03822001001000000",
    package_code: "1476764",
    descriptionKey: "items.120.description",
    imageBase: "индикатор стерил Медис-В-132-20,1000",
    price: 230000,
  },
  {
    id: 121,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.121.name",
    ikpuCode: "03822001001000000",
    package_code: "1476764",
    descriptionKey: "items.121.description",
    imageBase: "indikator vinar steritest-p-132-20-02",
    price: 399000,
  },
  {
    id: 122,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.122.name",
    ikpuCode: "03822001001000000",
    package_code: "1476764",
    imageBase: "Индикатор стерилизации Интест-П-1345",
    price: 227000,
    descriptionKey: "items.122.description",
  },
  {
    id: 123,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.123.name",
    ikpuCode: "09031001030000000",
    package_code: "1461323",
    descriptionKey: "items.123.description",
    imageBase: "Упаковочная машина PK100-C",
    price: 21900000,
  },
  {
    id: 124,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.124.name",
    ikpuCode: "09031001030000000",
    package_code: "1461323",
    descriptionKey: "items.124.description",
    imageBase: "Упаковочная машина  (Запечатывание) PK100-A (2)",
    price: 17800000,
  },
  {
    id: 125,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.125.name",
    ikpuCode: "09031001030000000",
    package_code: "1461323",
    descriptionKey: "items.125.description",
    imageBase: "Упаковочная машина, модель PK02-45 (2)",
    price: 4230000,
  },
  {
    id: 126,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.126.name",
    ikpuCode: "09031001030000000",
    package_code: "1461323",
    descriptionKey: "items.126.description",
    imageBase: "Упаковочная машина PKD310AB",
    price: 3250000,
  },
  {
    id: 127,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.127.name",
    ikpuCode: "09031001030000000",
    package_code: "1461323",
    descriptionKey: "items.127.description",
    imageBase: "Упаковочная машина, модель PKD300AB (2)",
    price: 2645000,
  },
  {
    id: 128,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.128.name",
    ikpuCode: "09031001030000000",
    package_code: "1461323",
    descriptionKey: "items.128.description",
    imageBase: "seal320-upakovochnaya-mashina",
    price: 2400000,
  },
  {
    id: 129,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.129.name",
    ikpuCode: "09031001030000000",
    package_code: "1461323",
    descriptionKey: "items.129.description",
    imageBase: "Подставка для крафт-пакетов трёхслойная PK213",
    price: 6880000,
  },
  {
    id: 130,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.130.name",
    ikpuCode: "09031001030000000",
    package_code: "1461323",
    descriptionKey: "items.130.description",
    imageBase: "Двухслойная подставка для крафт пакетов, модель PK212",
    price: 5270000,
  },
  {
    id: 131,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.131.name",
    ikpuCode: "09031001030000000",
    package_code: "1461323",
    descriptionKey: "items.131.description",
    imageBase: "Подставка для крафт-пакетов однослойная PK211",
    price: 4590000,
  },
  {
    id: 132,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.132.name",
    ikpuCode: "09018001229000000",
    package_code: "1460304",
    descriptionKey: "items.132.description",
    imageBase: "барьерная пленка 1",
    price: 139000,
  },
  {
    id: 133,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.133.name",
    ikpuCode: "09018001229000000",
    package_code: "1460304",
    descriptionKey: "items.133.description",
    imageBases: [
      "КРАФТ ПАКЕТ для стерилизации  50мм200 метр",
      "КРАФТ ПАКЕТ для стерилизации  55мм200 метр",
      "КРАФТ ПАКЕТ для стерилизации  75мм200 метр",
      "КРАФТ ПАКЕТ для стерилизации  100мм200 метр",
      "КРАФТ ПАКЕТ для стерилизации 150мм200 метр",
      "КРАФТ ПАКЕТ для стерилизации 200мм200 метр",
      "КРАФТ ПАКЕТ для стерилизации 250мм200 метр",
      "КРАФТ ПАКЕТ для стерилизации 300мм200 метр",
      "КРАФТ ПАКЕТ для стерилизации 350мм200 метр",
      "КРАФТ ПАКЕТ для стерилизации 400мм200 метр",
      "КРАФТ ПАКЕТ для стерилизации 450мм200 метр",
      "КРАФТ ПАКЕТ для стерилизации 500мм200 метр",
    ],
    sizes: [
      "variants.sizes.50b200",
      "variants.sizes.55b200",
      "variants.sizes.75b200",
      "variants.sizes.100b200",
      "variants.sizes.150b200",
      "variants.sizes.200b200",
      "variants.sizes.250b200",
      "variants.sizes.300b200",
      "variants.sizes.350b200",
      "variants.sizes.400b200",
      "variants.sizes.450b200",
      "variants.sizes.500b200",
    ],
    sizeImages: {
      "variants.sizes.50b200": "КРАФТ ПАКЕТ для стерилизации  50мм200 метр",
      "variants.sizes.55b200": "КРАФТ ПАКЕТ для стерилизации  55мм200 метр",
      "variants.sizes.75b200": "КРАФТ ПАКЕТ для стерилизации  75мм200 метр",
      "variants.sizes.100b200": "КРАФТ ПАКЕТ для стерилизации  100мм200 метр",
      "variants.sizes.150b200": "КРАФТ ПАКЕТ для стерилизации 150мм200 метр",
      "variants.sizes.200b200": "КРАФТ ПАКЕТ для стерилизации 200мм200 метр",
      "variants.sizes.250b200": "КРАФТ ПАКЕТ для стерилизации 250мм200 метр",
      "variants.sizes.300b200": "КРАФТ ПАКЕТ для стерилизации 300мм200 метр",
      "variants.sizes.350b200": "КРАФТ ПАКЕТ для стерилизации 350мм200 метр",
      "variants.sizes.400b200": "КРАФТ ПАКЕТ для стерилизации 400мм200 метр",
      "variants.sizes.450b200": "КРАФТ ПАКЕТ для стерилизации 450мм200 метр",
      "variants.sizes.500b200": "КРАФТ ПАКЕТ для стерилизации 500мм200 метр",
    },
    sizePrices: {
      "variants.sizes.50b200": 137000,
      "variants.sizes.55b200": 145000,
      "variants.sizes.75b200": 196000,
      "variants.sizes.100b200": 255000,
      "variants.sizes.150b200": 335000,
      "variants.sizes.200b200": 465000,
      "variants.sizes.250b200": 550000,
      "variants.sizes.300b200": 783000,
      "variants.sizes.350b200": 915000,
      "variants.sizes.400b200": 1049000,
      "variants.sizes.450b200": 1235000,
      "variants.sizes.500b200": 1366000,
    },
    sizesDescription: {
      "variants.sizes.50b200": "items.133.description.50b200",
      "variants.sizes.55b200": "items.133.description.55b200",
      "variants.sizes.75b200": "items.133.description.75b200",
      "variants.sizes.100b200": "items.133.description.100b200",
      "variants.sizes.150b200": "items.133.description.150b200",
      "variants.sizes.200b200": "items.133.description.200b200",
      "variants.sizes.250b200": "items.133.description.250b200",
      "variants.sizes.300b200": "items.133.description.300b200",
      "variants.sizes.350b200": "items.133.description.350b200",
      "variants.sizes.400b200": "items.133.description.400b200",
      "variants.sizes.450b200": "items.133.description.450b200",
      "variants.sizes.500b200": "items.133.description.500b200",
    },
  },
  {
    id: 134,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.134.name",
    ikpuCode: "09018001229000000",
    package_code: "1460304",
    descriptionKey: "items.134.description",
    imageBases: [
      "Самозапечатывающийся крафт пакет, 57x130 mm",
      "Самозапечатывающийся крафт пакет, 70x260 mm (2)",
      "Самозапечатывающийся крафт пакет, 70x350 mm",
      "Самозапечатывающийся крафт пакет, 135x260 mm",
      "Самозапечатывающийся крафт пакет, 135x280 mm",
      "Самозапечатывающийся крафт пакет, 190x360 mm (2)",
      "Самозапечатывающийся крафт пакет, 400x500 mm",
      "Самозапечатывающийся крафт пакет, 500x600 mm",
    ],
    sizes: [
      "variants.sizes.57b130",
      "variants.sizes.90b165",
      "variants.sizes.70b260",
      "variants.sizes.90b260",
      "variants.sizes.135b280",
      "variants.sizes.190b360",
      "variants.sizes.200b300",
      "variants.sizes.308b430",
      "variants.sizes.400b500",
      "variants.sizes.500b600",
    ],
    sizeImages: {
      "variants.sizes.57b130": "Самозапечатывающийся крафт пакет, 57x130 mm",
      "variants.sizes.70b260":
        "Самозапечатывающийся крафт пакет, 70x260 mm (2)",
      "variants.sizes.70b350": "Самозапечатывающийся крафт пакет, 70x350 mm",
      "variants.sizes.135b260": "Самозапечатывающийся крафт пакет, 135x260 mm",
      "variants.sizes.135b280": "Самозапечатывающийся крафт пакет, 135x280 mm",
      "variants.sizes.190b360":
        "Самозапечатывающийся крафт пакет, 190x360 mm (2)",
      "variants.sizes.400b500": "Самозапечатывающийся крафт пакет, 400x500 mm",
      "variants.sizes.500b600": "Самозапечатывающийся крафт пакет, 500x600 mm",
    },
    sizePrices: {
      "variants.sizes.57b130": 56000,
      "variants.sizes.90b165": 78000,
      "variants.sizes.70b260": 90000,
      "variants.sizes.90b260": 115000,
      "variants.sizes.135b280": 185000,
      "variants.sizes.190b360": 320000,
      "variants.sizes.200b300": 350000,
      "variants.sizes.308b430": 550000,
      "variants.sizes.400b500": 780000,
      "variants.sizes.500b600": 1200000,
    },
    sizesDescription: {
      "variants.sizes.57b130": "items.134.description.57x130",
      "variants.sizes.70b260": "items.134.description.70x260",
      "variants.sizes.70b350": "items.134.description.70x350",
      "variants.sizes.135b260": "items.134.description.135x260",
      "variants.sizes.135b280": "items.134.description.135x280",
      "variants.sizes.190b360": "items.134.description.190x360",
      "variants.sizes.400b500": "items.134.description.400x500",
      "variants.sizes.500b600": "items.134.description.500x600",
    },
  },
  {
    id: 135,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.135.name",
    ikpuCode: "03822001001000000",
    package_code: "1476764",
    descriptionKey: "items.135.description",
    imageBase: "индикатор для плазменной стерилизации (банка 250шт)",
    sizes: ["variants.sizes.250pcs", "variants.sizes.200pcs"],
    sizePrices: {
      "variants.sizes.250pcs": 299000,
      "variants.sizes.200pcs": 245000,
    },
  },
  {
    id: 136,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.136.name",
    ikpuCode: "03822001001000000",
    package_code: "1476764",
    descriptionKey: "items.136.description",
    imageBase: "индикатор для плазменной стерилизации 500шт",
    price: 599000,
  },
  {
    id: 137,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.137.name",
    ikpuCode: "03822001001000000",
    package_code: "1476764",
    descriptionKey: "items.137.description",
    imageBase: "Лента индикаторная для плазменной стерилизации (2)",
    price: 99000,
  },
  {
    id: 138,
    vat_percent: 12,
    category: "surgery",
    nameKey: "items.138.name",
    ikpuCode: "09018001229000000",
    package_code: "1460304",
    descriptionKey: "items.138.description",
    imageBase: "Нетканые материалы (10 штпачка) (2)",
    price: 95000,
  },
  {
    id: 139,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.139.name",
    ikpuCode: "09018001229000000",
    package_code: "1460304",
    imageBases: [
      "КРАФТ ПАКЕТ для Плазменный STANDART  75мм70 метр (2)",
      "КРАФТ ПАКЕТ для Плазменный  STANDART  100мм70 метр",
      "КРАФТ ПАКЕТ для Плазменный STANDART  150мм70 метр",
      "КРАФТ ПАКЕТ для Плазменный STANDART  200мм70 метр",
      "КРАФТ ПАКЕТ для Плазменный STANDART  250мм70 метр",
      "КРАФТ ПАКЕТ для Плазменный STANDART  300мм70 метр (2)",
      "КРАФТ ПАКЕТ для Плазменный STANDART  350мм70 метр",
      "КРАФТ ПАКЕТ для Плазменный STANDART  400мм70 метр",
      "КРАФТ ПАКЕТ для Плазменный STANDART  450мм70 метр",
      "КРАФТ ПАКЕТ для Плазменный STANDART  500мм70 метр",
      "КРАФТ ПАКЕТ для Плазменный STANDART  75мм100 метр",
      "КРАФТ ПАКЕТ для Плазменный STANDART  100мм100 метр",
      "КРАФТ ПАКЕТ для Плазменный STANDART  150мм100 метр",
      "КРАФТ ПАКЕТ для Плазменный STANDART  200мм100 метр",
      "КРАФТ ПАКЕТ для Плазменный STANDART  250мм100 метр",
      "КРАФТ ПАКЕТ для Плазменный STANDART  400мм100 метр",
    ],
    sizes: [
      "variants.sizes.75b70",
      "variants.sizes.100b70",
      "variants.sizes.150b70",
      "variants.sizes.200b70",
      "variants.sizes.250b70",
      "variants.sizes.300b70",
      "variants.sizes.350b70",
      "variants.sizes.400b70",
      "variants.sizes.450b70",
      "variants.sizes.500b70",
      "variants.sizes.75b100",
      "variants.sizes.100b100",
      "variants.sizes.150b100",
      "variants.sizes.200b100",
      "variants.sizes.250b100",
      "variants.sizes.300b100",
      "variants.sizes.350b100",
      "variants.sizes.400b100",
    ],
    sizeImages: {
      "variants.sizes.75b70":
        "КРАФТ ПАКЕТ для Плазменный STANDART  75мм70 метр (2)",
      "variants.sizes.100b70":
        "КРАФТ ПАКЕТ для Плазменный  STANDART  100мм70 метр",
      "variants.sizes.150b70":
        "КРАФТ ПАКЕТ для Плазменный STANDART  150мм70 метр",
      "variants.sizes.200b70":
        "КРАФТ ПАКЕТ для Плазменный STANDART  200мм70 метр",
      "variants.sizes.250b70":
        "КРАФТ ПАКЕТ для Плазменный STANDART  250мм70 метр",
      "variants.sizes.300b70":
        "КРАФТ ПАКЕТ для Плазменный STANDART  300мм70 метр (2)",
      "variants.sizes.350b70":
        "КРАФТ ПАКЕТ для Плазменный STANDART  350мм70 метр",
      "variants.sizes.400b70":
        "КРАФТ ПАКЕТ для Плазменный STANDART  400мм70 метр",
      "variants.sizes.450b70":
        "КРАФТ ПАКЕТ для Плазменный STANDART  450мм70 метр",
      "variants.sizes.500b70":
        "КРАФТ ПАКЕТ для Плазменный STANDART  500мм70 метр",
      "variants.sizes.75b100":
        "КРАФТ ПАКЕТ для Плазменный STANDART  75мм100 метр",
      "variants.sizes.100b100":
        "КРАФТ ПАКЕТ для Плазменный STANDART  100мм100 метр",
      "variants.sizes.150b100":
        "КРАФТ ПАКЕТ для Плазменный STANDART  150мм100 метр",
      "variants.sizes.200b100":
        "КРАФТ ПАКЕТ для Плазменный STANDART  200мм100 метр",
      "variants.sizes.250b100":
        "КРАФТ ПАКЕТ для Плазменный STANDART  250мм100 метр",
      "variants.sizes.400b100":
        "КРАФТ ПАКЕТ для Плазменный STANDART  400мм100 метр",
    },
    sizePrices: {
      "variants.sizes.75b70": 467000,
      "variants.sizes.100b70": 569000,
      "variants.sizes.150b70": 678000,
      "variants.sizes.200b70": 876000,
      "variants.sizes.250b70": 1079000,
      "variants.sizes.300b70": 1251000,
      "variants.sizes.350b70": 1414000,
      "variants.sizes.400b70": 1565000,
      "variants.sizes.450b70": 1839000,
      "variants.sizes.500b70": 2088000,
      "variants.sizes.75b100": 667000,
      "variants.sizes.100b100": 813000,
      "variants.sizes.150b100": 968000,
      "variants.sizes.200b100": 1251000,
      "variants.sizes.250b100": 1541000,
      "variants.sizes.300b100": 1787000,
      "variants.sizes.350b100": 2020000,
      "variants.sizes.400b100": 2236000,
    },
    sizesDescription: {
      "variants.sizes.75b70": "items.139.description.75b70",
      "variants.sizes.100b70": "items.139.description.100b70",
      "variants.sizes.150b70": "items.139.description.150b70",
      "variants.sizes.200b70": "items.139.description.200b70",
      "variants.sizes.250b70": "items.139.description.250b70",
      "variants.sizes.300b70": "items.139.description.300b70",
      "variants.sizes.350b70": "items.139.description.350b70",
      "variants.sizes.400b70": "items.139.description.400b70",
      "variants.sizes.450b70": "items.139.description.450b70",
      "variants.sizes.500b70": "items.139.description.500b70",
      "variants.sizes.75b100": "items.139.description.75b100",
      "variants.sizes.100b100": "items.139.description.100b100",
      "variants.sizes.150b100": "items.139.description.150b100",
      "variants.sizes.200b100": "items.139.description.200b100",
      "variants.sizes.250b100": "items.139.description.250b100",
      "variants.sizes.400b100": "items.139.description.400b100",
    },
  },
  {
    id: 140,
    vat_percent: 12,
    category: "dressings",
    nameKey: "items.140.name",
    ikpuCode: "09018001229000000",
    package_code: "1460304",
    imageBases: [
      "КРАФТ ПАКЕТ для Плазменный PREMIUM  75мм70 метр",
      "КРАФТ ПАКЕТ для Плазменный PREMIUM  100мм70 метр",
      "КРАФТ ПАКЕТ для Плазменный PREMIUM  150мм70 метр (2)",
      "КРАФТ ПАКЕТ для Плазменный PREMIUM  200мм70 метр (2)",
      "КРАФТ ПАКЕТ для Плазменный PREMIUM  250мм70 метр (2)",
    ],
    sizes: [
      "variants.sizes.75b70",
      "variants.sizes.100b70",
      "variants.sizes.150b70",
      "variants.sizes.200b70",
      "variants.sizes.250b70",
      "variants.sizes.300b70",
    ],
    sizeImages: {
      "variants.sizes.75b70": "КРАФТ ПАКЕТ для Плазменный PREMIUM  75мм70 метр",
      "variants.sizes.100b70":
        "КРАФТ ПАКЕТ для Плазменный PREMIUM  100мм70 метр",
      "variants.sizes.150b70":
        "КРАФТ ПАКЕТ для Плазменный PREMIUM  150мм70 метр (2)",
      "variants.sizes.200b70":
        "КРАФТ ПАКЕТ для Плазменный PREMIUM  200мм70 метр (2)",
      "variants.sizes.250b70":
        "КРАФТ ПАКЕТ для Плазменный PREMIUM  250мм70 метр (2)",
    },
    sizePrices: {
      "variants.sizes.75b70": 539000,
      "variants.sizes.100b70": 654000,
      "variants.sizes.150b70": 793000,
      "variants.sizes.200b70": 1028000,
      "variants.sizes.250b70": 1269000,
      "variants.sizes.300b70": 1491000,
    },
    sizesDescription: {
      "variants.sizes.75b70": "items.140.description.75b70",
      "variants.sizes.100b70": "items.140.description.100b70",
      "variants.sizes.150b70": "items.140.description.150b70",
      "variants.sizes.200b70": "items.140.description.200b70",
      "variants.sizes.250b70": "items.140.description.250b70",
      "variants.sizes.300b70": "items.140.description.300b70",
    },
  },
  {
    id: 141,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.141.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.141.description",
    imageBases: ["иглы спинальной анестезии тип Pencil Point 25g-1"],
    sizes: ["variants.sizes.25G", "variants.sizes.26g"],
    price: 16240,
  },
  {
    id: 142,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.142.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.142.description",
    imageBases: ["Иглы спинальной анестезии, тип Pencil Point 26g"],
    sizes: ["variants.sizes.25G", "variants.sizes.26g"],
    price: 28560,
  },
  {
    id: 143,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.143.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.143.description",
    imageBase: "иглы спинальной анестезии тип Pencil Point 25g-1",
    price: 22000,
  },
  {
    id: 144,
    vat_percent: 12,
    category: "injection",
    nameKey: "items.144.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.144.description",
    imageBases: ["Иглы спинальной анестезии, тип Pencil Point 26g"],
    sizes: ["variants.sizes.25G", "variants.sizes.26g"],
    price: 38000,
  },
  {
    id: 145,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.145.name",
    ikpuCode: "03006001003000000",
    package_code: "1282172",
    descriptionKey: "items.145.description",
    imageBase: "Гель для узи Акугель 5кг в канистре-2",
    price: 165000,
  },
  {
    id: 146,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.146.name",
    ikpuCode: "03006001003000000",
    package_code: "1282172",
    descriptionKey: "items.146.description",
    imageBase:
      "Гель для узи Акугель высокой вязкости, 5кг в канистре в комплекте с дозатором",
    price: 258000,
  },
  {
    id: 147,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.147.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.147.description",
    imageBases: [
      "лента диаграммная для ЭКГ 80x20",
      "лента диаграммная для ЭКГ 110x30",
      "лента диаграммная для ЭКГ 210x30",
      "лента диаграммная для ЭКГ 215x30",
    ],
    sizes: [
      "variants.sizes.80b20",
      "variants.sizes.110b30",
      "variants.sizes.210b30",
      "variants.sizes.215b30",
    ],
    sizeImages: {
      "variants.sizes.80b20": "лента диаграммная для ЭКГ 80x20",
      "variants.sizes.110b30": "лента диаграммная для ЭКГ 110x30",
      "variants.sizes.210b30": "лента диаграммная для ЭКГ 210x30",
      "variants.sizes.215b30": "лента диаграммная для ЭКГ 215x30",
    },
    sizePrices: {
      "variants.sizes.80b20": 9800,
      "variants.sizes.110b30": 30000,
      "variants.sizes.210b30": 55000,
      "variants.sizes.215b30": 57000,
    },
    sizesDescription: {
      "variants.sizes.80b20": "items.147.description.80b20",
      "variants.sizes.110b30": "items.147.description.110b30",
      "variants.sizes.210b30": "items.147.description.210b30",
      "variants.sizes.215b30": "items.147.description.215b30",
    },
  },
  {
    id: 148,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.148.name",
    ikpuCode: "06302001001000000",
    package_code: "1373758",
    descriptionKey: "items.148.description",
    imageBases: [
      "rulon prostina-oq-rang",
      "rulon prostina-kok-rang",
      "rulon prostina-pushti",
      "rulon prostina-siyohrang",
      "rulon prostina-sabzirang",
    ],
    colors: [
      "variants.colors.white",
      "variants.colors.blue",
      "variants.colors.pink",
      "variants.colors.purple",
      "variants.colors.orange",
    ],
    colorImages: {
      "variants.colors.white": "rulon prostina-oq-rang",
      "variants.colors.blue": "rulon prostina-kok-rang",
      "variants.colors.pink": "rulon prostina-pushti",
      "variants.colors.purple": "rulon prostina-siyohrang",
      "variants.colors.orange": "rulon prostina-sabzirang",
    },
    price: 210000,
  },
  {
    id: 149,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.149.name",
    ikpuCode: "06302001001000000",
    package_code: "1373758",
    imageBase: "Одноразовые простыни 20 гм(упаковка)",
    colors: [
      "variants.colors.white",
      "variants.colors.blue",
      "variants.colors.pink",
      "variants.colors.purple",
      "variants.colors.orange",
    ],
    descriptionKey: "items.149.description",
    price: 26000,
  },
  {
    id: 150,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.150.name",
    ikpuCode: "06302001001000000",
    package_code: "1373758",
    imageBase: "Одноразовые простыни рулонные UKM",
    descriptionKey: "items.150.description",
    price: 240000,
  },
  {
    id: 151,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.151.name",
    ikpuCode: "05603001013000000",
    package_code: "1507910",
    imageBase: "Салфетки одноразовые 15 гм² в рулоне",
    colors: [
      "variants.colors.white",
      "variants.colors.blue",
      "variants.colors.pink",
    ],
    descriptionKey: "items.151.description",
    price: 110000,
  },
  {
    id: 152,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.152.name",
    imageBase: "Одноразовое полотенце в рулоне 35 × 70",
    ikpuCode: "05603001013000000",
    package_code: "1507910",
    descriptionKey: "items.152.description",
    price: 170000,
  },
  {
    id: 153,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.153.name",
    ikpuCode: "04803001001000000",
    package_code: "1477226",
    descriptionKey: "items.153.description",
    imageBases: [
      "Салфетки НАГРУДНИКИ  розовый, №125",
      "Салфетки НАГРУДНИКИ  синий №125 (2)",
      "Салфетки НАГРУДНИКИ  зелёный  №125",
      "Салфетки НАГРУДНИКИ  фиолетвый, №125",
      "Салфетки НАГРУДНИКИ  черный №125 (2)",
    ],
    colors: [
      "variants.colors.pink",
      "variants.colors.blue",
      "variants.colors.green",
      "variants.colors.purple",
      "variants.colors.black",
    ],
    colorImages: {
      "variants.colors.pink": "Салфетки НАГРУДНИКИ  розовый, №125",
      "variants.colors.blue": "Салфетки НАГРУДНИКИ  синий №125 (2)",
      "variants.colors.green": "Салфетки НАГРУДНИКИ  зелёный  №125",
      "variants.colors.purple": "Салфетки НАГРУДНИКИ  фиолетвый, №125",
      "variants.colors.black": "Салфетки НАГРУДНИКИ  черный №125 (2)",
    },
    price: 64000,
  },
  {
    id: 154,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.154.name",
    ikpuCode: "04803001001000000",
    package_code: "1477226",
    imageBases: ["Bir martalik stomatologik ko‘krakchalar 45 × 33 (2)", "Bir martalik stomatologik ko‘krakchalar 45 × 33"],
    descriptionKey: "items.154.description",
    price: 55000,
  },
  {
    id: 155,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.155.name",
    ikpuCode: "06302002003012004",
    package_code: "1367235",
    descriptionKey: "items.155.description",
    imageBase: "спиртовые салфетки №100-2",
    price: 26000,
  },
  {
    id: 156,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.156.name",
    ikpuCode: "05603001013000000",
    package_code: "1507910",
    descriptionKey: "items.156.description",
    imageBases: ["шортики_синие", "шортики_черные"],
    colors: ["variants.colors.blue", "variants.colors.black"],
    colorImages: {
      "variants.colors.blue": "шортики_синие",
      "variants.colors.black": "шортики_черные",
    },
    price: 3500,
  },
  {
    id: 157,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.157.name",
    ikpuCode: "09018010033000000",
    package_code: "1492604",
    descriptionKey: "items.157.description",
    imageBase: "пробирки с активатором сверт-я, красная крышка,13x75мм 5мл",
    price: 105000,
  },
  {
    id: 158,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.158.name",
    ikpuCode: "09018010033000000",
    package_code: "1492604",
    descriptionKey: "items.158.description",
    imageBase: "пробирки с активатором сверт-я, красная крышка,13x75мм 6мл",
    price: 110000,
  },
  {
    id: 159,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.159.name",
    ikpuCode: "09018010033000000",
    package_code: "1492604",
    descriptionKey: "items.159.description",
    imageBase: "пробирки с 3,2 цитратом натрия, синий крышка 13x75мм, 2,7мл-2",
    price: 195000,
  },
  {
    id: 160,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.160.name",
    ikpuCode: "09018010033000000",
    package_code: "1492604",
    descriptionKey: "items.160.description",
    imageBase:
      "пробирки с активатором свертывания и гелем, желтая крышка 13x100мм 5мл-2",
    price: 195000,
  },
  {
    id: 161,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.161.name",
    ikpuCode: "09018010033000000",
    package_code: "1492604",
    descriptionKey: "items.161.description",
    imageBase: "пробирки с ЭДТА КЗ, фиолетовая крышка, 13x75мм, 3мл",
    price: 105000,
  },
  {
    id: 162,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.162.name",
    ikpuCode: "09018010033000000",
    package_code: "1492604",
    descriptionKey: "items.162.description",
    imageBase: "пробирки  с литий-гепарином, зеленая крышка, 13x75мм 5мл-2",
    price: 140000,
  },
  {
    id: 163,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.163.name",
    ikpuCode: "09018010033000000",
    package_code: "1492604",
    descriptionKey: "items.163.description",
    imageBase:
      "пробирки с гель натрий-гепарином, зеленая крышка, 16x100мм, 8,5мл-2",
    price: 537600,
  },
  {
    id: 164,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.164.name",
    ikpuCode: "03926001203000000",
    package_code: "1541749",
    descriptionKey: "items.164.description",
    imageBase:
      "EP-150-E-J Микроцентрифужная пробирка 1,5 мл, тип Эппендорф, в упаковке 500 шт. БЕЗ ДНКРНК (2)",
    price: 130000,
  },
  {
    id: 165,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.165.name",
    ikpuCode: "03926001203000000",
    package_code: "1541749",
    descriptionKey: "items.165.description",
    imageBase:
      "Пробирки 1.5ml для гематологических анализаторов с EDTA K3 напылением (500 шт в упаковке)",
    price: 230000,
  },
  {
    id: 166,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.166.name",
    ikpuCode: "09018001080000000",
    package_code: "1460392",
    descriptionKey: "items.166.description",
    imageBases: [
      "Игла бабочки ДЛЯ ПРОБИРКИ (22G)",
      "Игла бабочки ДЛЯ ПРОБИРКИ (23G)",
    ],
    sizes: ["variants.sizes.22g", "variants.sizes.23g"],
    sizeImages: {
      "variants.sizes.22g": "Игла бабочки ДЛЯ ПРОБИРКИ (22G)",
      "variants.sizes.23g": "Игла бабочки ДЛЯ ПРОБИРКИ (23G)",
    },
    price: 165000,
  },
  {
    id: 167,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.167.name",
    ikpuCode: "09018001080000000",
    package_code: "1460392",
    descriptionKey: "items.167.description",
    imageBases: [
      "Иглы к пробиркам для вакуумного забора крови, 100 шт. в упак. Размеры 21G",
      "Иглы к пробиркам для вакуумного забора крови, 100 шт. в упак. Размеры 22G",
      "Иглы к пробиркам для вакуумного забора крови, 100 шт. в упак. Размеры  23G",
    ],
    sizes: ["variants.sizes.21g", "variants.sizes.22g", "variants.sizes.23g"],
    sizeImages: {
      "variants.sizes.21g":
        "Иглы к пробиркам для вакуумного забора крови, 100 шт. в упак. Размеры 21G",
      "variants.sizes.22g":
        "Иглы к пробиркам для вакуумного забора крови, 100 шт. в упак. Размеры 22G",
      "variants.sizes.23g":
        "Иглы к пробиркам для вакуумного забора крови, 100 шт. в упак. Размеры  23G",
    },
    price: 165000,
  },
  {
    id: 168,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.168.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.168.description",
    imageBase: "Держатель иглы с защитной крышкой-2",
    price: 830,
  },
  {
    id: 169,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.169.name",
    ikpuCode: "03926001020010036",
    package_code: "1423960",
    descriptionKey: "items.169.description",
    imageBases: [
      "пипет-дозатор MicroPette plus, с регулируемым объемом 20-200мкл",
      "пипет-дозатор MicroPette plus, с регулируемым объемом 20-200мкл-2",
    ],
    price: 550000,
  },
  {
    id: 170,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.170.name",
    ikpuCode: "03926001020010036",
    package_code: "1423960",
    descriptionKey: "items.170.description",
    imageBase: "наконечники для дозаторов 1000 мкл (в пачке 500 шт)",
    price: 80000,
  },
  {
    id: 171,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.171.name",
    descriptionKey: "items.171.description",
    imageBase: "наконечники для дозаторов 200 мкл (в пачке 1000шт) желтая",
    price: 65000,
  },
  {
    id: 172,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.172.name",
    descriptionKey: "items.172.description",
    imageBase: "наконечники для дозаторов 200 мкл (в пачке 1000шт) желтая",
    price: 110000,
  },
  {
    id: 173,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.173.name",
    ikpuCode: "03923002002000000",
    package_code: "1480099",
    sizes: [
      "variants.sizes.60mlws",
      "variants.sizes.60mlwths",
      "variants.sizes.100mlws",
      "variants.sizes.100mlwths",
    ],
    imageBases: [
      "контейнер для сбора биоматериалов с крышкой пластыковый",
      "контейнер для сбора биоматериалов с крышкой и ложкой пластыковый-2",
      "контейнер для сбора биоматериалов с крышкой и ложкой пластыковый",
    ],
    sizeImages: {
      "variants.sizes.60mlws":
        "контейнер для сбора биоматериалов с крышкой пластыковый",
      "variants.sizes.60mlwths":
        "контейнер для сбора биоматериалов с крышкой и ложкой пластыковый-2",
      "variants.sizes.100mlwths":
        "контейнер для сбора биоматериалов с крышкой и ложкой пластыковый",
    },
    sizesDescription: {
      "variants.sizes.60mlws": "items.173.description.60ml",
      "variants.sizes.60mlwths": "items.173.description.60mlWS",
      "variants.sizes.100mlws": "items.173.description.100ml",
      "variants.sizes.100mlwths": "items.173.description.100mlWS",
    },
    sizePrices: {
      "variants.sizes.60mlws": 1800,
      "variants.sizes.60mlwths": 2000,
      "variants.sizes.100mlws": 2200,
      "variants.sizes.100mlwths": 2300,
    },
  },
  {
    id: 174,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.174.name",
    ikpuCode: "04014001013000000",
    package_code: "1340642",
    descriptionKey: "items.174.description",
    imageBases: ["жгут латексный", "жгут латексный-2"],
    price: 4000,
  },
  {
    id: 175,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.175.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.175.description",
    imageBases: [
      "электрохирургический карандаш",
      "электрохирургический карандаш-2",
    ],
    price: 50000,
  },
  {
    id: 176,
    vat_percent: 12,
    category: "hygiene",
    nameKey: "items.176.name",
    ikpuCode: "07017001031000000",
    package_code: "1520312",
    descriptionKey: "items.176.description",
    sizes: ["variants.sizes.18mm", "variants.sizes.24mm"],
    imageBase: "cover glass 18mmx18mm",
    sizePrices: {
      "variants.sizes.18mm": 87000,
      "variants.sizes.24mm": 145000,
    },
  },
  {
    id: 177,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.177.name",
    ikpuCode: "07003001008000000",
    package_code: "1343480",
    descriptionKey: "items.177.description",
    imageBase: "Предметное стекло без шлиф.краями (в упак. 72 шт)",
    price: 34000,
  },
  {
    id: 178,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.178.name",
    ikpuCode: "09018001229005003",
    package_code: "1484926",
    descriptionKey: "items.178.description",
    imageBases: [
      "Измеритель артериального давления LDH-MAXIC",
      "Измеритель артериального давления LDH-MAXIC-2",
      "Измеритель артериального давления LDH-MAXIC-3",
    ],
    price: 134000,
  },
  {
    id: 179,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.179.name",
    ikpuCode: "09018001229005003",
    package_code: "1484926",
    descriptionKey: "items.179.description",
    imageBases: [
      "Измеритель артериального давления ИАД-01 Адьютор-2",
      "Измеритель артериального давления ИАД-01 Адьютор",
      "Измеритель артериального давления ИАД-01 Адьютор-3",
    ],
    price: 185000,
  },
  {
    id: 180,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.180.name",
    ikpuCode: "09018010033000000",
    package_code: "1489938",
    descriptionKey: "items.180.description",
    imageBase: "stetoskop",
    price: 65000,
  },
  {
    id: 181,
    vat_percent: 12,
    category: "lab",
    nameKey: "items.181.name",
    ikpuCode: "09018001229000000",
    package_code: "1460304",
    descriptionKey: "items.181.description",
    imageBase: "термометр медицинский ртутный",
    price: 15000,
  },
  {
    id: 182,
    vat_percent: 12,
    category: "equipment",
    nameKey: "items.182.name",
    ikpuCode: "06307003001000000",
    sizePackageCodes: {
      "variants.sizes.pack5": "1778348",
      "variants.sizes.box25": "1766219",
      "variants.sizes.box50": "1658211",
    },
    descriptionKey: "items.182.description",
    imageBase: "тонометр механический LDH-ANEROID-2",
    imageBases: [
      "Clinic clean одноразовая маска белый цвет 5шт",
      "Clinic clean одноразовая маска в коробке 25 шт",
      "Clinic clean одноразовая маска в пачке 50шт",
    ],
    colors: [
      "variants.colors.white",
      "variants.colors.blue",
      "variants.colors.black",
      "variants.colors.pink",
    ],
    sizes: [
      "variants.sizes.pack5",
      "variants.sizes.box25",
      "variants.sizes.box50",
    ],
    sizePrices: {
      "variants.sizes.pack5": 3640,
      "variants.sizes.box25": 18200,
      "variants.sizes.box50": 36400,
    },
  },
  {
    id: 183,
    vat_percent: 12,
    category: "equipment",
    nameKey: "items.183.name",    
    ikpuCode: "06307003001000000",
    package_code: "1778348",
    descriptionKey: "items.183.description",
    imageBases: [
      "Маска гигиеническая Детская РОЗОВЫЙ №10",
      "Маска гигиеническая Детская БЕЛЫЙ №10",
      "Маска гигиеническая Детская СИНИЙ №10",
    ],
    colors: [
      "variants.colors.pink",
      "variants.colors.white",
      "variants.colors.blue",
    ],
    colorImages: {
      "variants.colors.pink": "Маска гигиеническая Детская РОЗОВЫЙ №10",
      "variants.colors.white": "Маска гигиеническая Детская БЕЛЫЙ №10",
      "variants.colors.blue": "Маска гигиеническая Детская СИНИЙ №10",
    },
    price: 8000,
  },
  {
    id: 184,
    vat_percent: 12,
    category: "equipment",
    nameKey: "items.184.name",
    ikpuCode: "06307003001000000",
    package_code: "1658211",
    descriptionKey: "items.184.description",
    imageBases: [
      "Маска гигиеническая Детская  БЕЛЫЙ №50",
      "Маска гигиеническая Детская РОЗОВЫЙ №50",
      "Маска гигиеническая Детская СИНИЙ №50",
    ],
    colors: [
      "variants.colors.pink",
      "variants.colors.white",
      "variants.colors.blue",
    ],
    colorImages: {
      "variants.colors.pink": "Маска гигиеническая Детская РОЗОВЫЙ №50",
      "variants.colors.white": "Маска гигиеническая Детская БЕЛЫЙ №50",
      "variants.colors.blue": "Маска гигиеническая Детская СИНИЙ №50",
    },
    price: 40000,
  },

  {
    id: 185,
    category: "equipment",
    vat_percent: 12,
    nameKey: "items.185.name",
    ikpuCode: "06307003001000000",
    package_code: "1658211",
    descriptionKey: "items.185.description",
    imageBase: "Маски для лица  EPFM Ecoproduct в пачке 50шт",
    price: 20000,
  },
  {
    id: 186,
    category: "equipment",
    vat_percent: 12,
    nameKey: "items.186.name",
    ikpuCode: "06307003001000000",
    package_code: "1658211",
    descriptionKey: "items.186.description",
    imageBase: "Маски для лица  EPFM Ecoproduct в пачке 50шт (2)",
    price: 20000,
  },
];

export const categories = [
  { key: "all", name: "All Products", icon: "Package" },
  { key: "injection", image: categoryInjection },
  { key: "equipment", image: categoryEquipment },
  { key: "surgery", image: categorySurgery },
  { key: "hygiene", image: categoryHygiene },
  { key: "dressings", image: categoryDressings },
  { key: "lab", image: categoryLab },
];
